import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FormProvider,
  SubmitErrorHandler,
  SubmitHandler,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore lodash is in the parent dir

import { Button } from 'components/Button';
import {
  QuickQuoteCoach,
  type Suggestion,
  applySuggestion,
  resolveLiveSuggestion,
  shouldShowCoach,
} from 'components/Kit/QuickQuoteCoach';
import { LaneHistoryFromServiceChart } from 'components/LaneHistoryFromServiceChart';
import { QuoteCardType } from 'components/QuoteCard';
import { Separator } from 'components/Separator';
import { TooltipProvider } from 'components/Tooltip';
import { Flex } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import CarrierPriceCalculator, {
  CarrierPriceCalculatorParent,
} from 'components/pricing/CarrierPriceCalculator';
import { Typography } from 'components/typography';
import {
  QuotingPortalPageStateUpdatedAction,
  UpdateQuoteRequestDataAction,
} from 'constants/BackgroundScript';
import { ExtendedFormProvider } from 'contexts/extendedFormContext';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { useToast } from 'hooks/useToaster';
import DrumkitLogo from 'icons/DrumkitLogo';
import { getCustomerLTLAccessorials } from 'lib/api/getCustomerLTLAccessorials';
import {
  type LTLQuickQuoteResponse,
  getLTLQuickQuoteResult,
  getLTLQuickQuotes,
} from 'lib/api/getLTLQuickQuotes';
import { getUserDATInfo } from 'lib/api/getUserDATInfo';
import { resolveQuickQuoteSourceContext } from 'lib/api/resolveQuickQuoteSourceContext';
import { UpdateLaneRateRequest, updateRateView } from 'lib/api/updateRateView';
import { getCurrentTab, getTabHTML } from 'lib/chromeScripting/util';
import {
  emergeSpotBidFormPresentInHtml,
  isEmergeMarketHost,
  isEmergeMarketSpotClosedPath,
  isEmergeMarketSpotPath,
} from 'lib/hosts/quoting/emergemarket';
import { QuotingPortal, newQuotingPortal } from 'lib/hosts/quoting/interface';
import { buildLTLTaiRequest } from 'pages/QuoteView/Quoting/RequestQuickQuote/LTL/api/buildLTLTaiRequest';
import { buildLTLThreeGRequest } from 'pages/QuoteView/Quoting/RequestQuickQuote/LTL/api/buildLTLThreeGRequest';
import { buildLTLTurvoRequest } from 'pages/QuoteView/Quoting/RequestQuickQuote/LTL/api/buildLTLTurvoRequest';
import { applyMissingShiplifyLookups } from 'pages/QuoteView/Quoting/RequestQuickQuote/LTL/applyShiplifyLookups';
import {
  getShiplifyAddressKeys,
  getShiplifyStopsToCheck,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/LTL/shiplifyLookup';
import {
  QuoteTerminatingAction,
  QuoteValueType,
  SUBMIT_PORTAL_BID_USER_FACING_FAILURE_MESSAGE,
  SubmitBidStatusVariant,
  getLtlQuotePollIntervalMs,
  getLtlQuotePollMaxAttempts,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/constants';
import {
  DATQuoteLocationType,
  DATQuoteTimeframe,
  ProfitType,
  QuoteCountries,
  QuoteMode,
  QuoteTypeInSource,
  SelectedQuoteType,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { Email } from 'types/Email';
import { Stop } from 'types/QuoteRequest';
import { Maybe, MaybeUndef, Undef } from 'types/UtilityTypes';
import ButtonText from 'types/enums/ButtonText';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import {
  AccessorialIntegration,
  Quoting,
  QuotingPortals,
  TMS,
} from 'types/enums/Integrations';
import { SuggestionPipelines } from 'types/suggestions/CoreSuggestions';
import {
  QuoteChanges,
  SuggestedQuoteChange,
} from 'types/suggestions/QuoteSuggestions';
import captureException from 'utils/captureException';
import convertZipPlus4ToRegular from 'utils/convertExtendedZipToRegular';
import { handleFormErrors } from 'utils/formValidation/formErrorHandler';
import { titleCase } from 'utils/formatStrings';
import { getNewCustomerURLInTMS } from 'utils/getNewCustomerURLInTMS';
import { getQuoteURLInTMS } from 'utils/getQuoteURLInTMS';
import { isQuoteRequestActionable } from 'utils/isQuoteRequestActionable';
import { normalizeToArray } from 'utils/loadInfoAndBuilding';
import { calculatePricing } from 'utils/priceCalculations';
import { cn } from 'utils/shadcn';

import { CustomAccessorialsSection } from './FTL/CustomAccessorialsSection';
import { QuoteCardsSection } from './FTL/QuoteCardsSection';
import { QuoteInformationSection } from './FTL/QuoteInformationSection';
import { QuoteSubmissionSection } from './FTL/QuoteSubmissionSection';
import { RouteDisplaySection } from './FTL/RouteDisplaySection';
import { StopsSection } from './FTL/StopsSection';
import { LTLFormRouter } from './LTL/LTLFormRouter';
import { LTLQuoteResults } from './LTL/LTLQuoteResults';
import { SHIPLIFY_STOP_TYPES } from './LTL/shiplifyConsts';
import { DATLaneHistory } from './LaneHistory/DATLaneHistory';
import { LaneHistoryTrends } from './LaneHistory/LaneHistoryTrends';
import { QuoteModeToggle } from './QuoteModeToggle';
import { BidFlowMode, BidFlowToggle } from './common/BidFlowToggle';
import { DATAccessSection } from './common/DATAccessSection';
import { DraftResponseSection } from './common/DraftResponseSection';
import {
  SubmitBidPayload,
  SubmitBidSection,
  type SubmitBidStatus,
} from './common/SubmitBidSection';
import {
  BidFlowCommittedContext,
  decideBidFlowContextReset,
} from './common/bidFlowContextReset';
import {
  getServerSubmitPortalNamesForQuoteMode,
  resolveDefaultServerSubmitPortalName,
} from './common/submitBidPortalPolicy';
import {
  isSubmitBidThreadEligible,
  shouldDefaultToSubmitBidFlow,
} from './common/submitBidThreadEligibility';
import { FloatingSellRate } from './components/FloatingSellRate';
import { useQuickQuoteContext } from './context/QuickQuoteContext';
import {
  type PortalSubmitInlineFeedback,
  type SubmitPortalOverrides,
  useDistanceCalculation,
  useFTLDraftResponse,
  useIsOnQuotingPortal,
  useLTLDraftResponse,
  usePortalCredentialOptionsBatch,
  useQuickQuoteDefaultValues,
  useQuoteCardSelection,
  useQuoteConfirmationPing,
  useQuoteHandlers,
  useQuoteSubmission,
  useQuoteSubmissionViaURL,
} from './hooks';
import { useHasSuperDispatchQuoting } from './hooks/useHasSuperDispatchQuoting';
import { useQuickQuoteCurrency } from './hooks/useQuickQuoteCurrency';
import {
  LTL_LEGACY_EMPTY_QUOTES_MESSAGE,
  LTL_LEGACY_SYSTEM_FAILURE_MESSAGE,
  LTL_ZERO_QUOTE_OUTCOME,
  type LtlZeroQuoteOutcome,
  getLtlProviderOutcomeConfig,
} from './ltlProviderOutcomeConfig';
import {
  CarrierCostType,
  LTLQuote,
  QuickQuoteInputs,
  QuoteFormStop,
  getSubTransportTypeOptions,
  getTransportTypeOptions,
} from './types';
import { getLTLQuoteIdentity } from './util';
import {
  fetchLaneHistoryFromService,
  getDefaultLaneHistoryTierIndex,
  getLaneHistoryTierDisplay,
  validateLTLForm,
} from './utils';
import { classifyLTLQuoteResult } from './utils/classifyLTLQuoteResult';
import { fetchSuggestedCustomer } from './utils/fetchSuggestedCustomer';
import { getSelectedQuickQuoteId as resolveSelectedQuickQuoteId } from './utils/getSelectedQuickQuoteId';
import {
  getSubmitBidLoadIdNormalizer,
  shouldPreserveInvalidSubmitBidLoadId,
} from './utils/getSubmitBidLoadIdNormalizer';
import { parseLocation } from './utils/location';
import { PCMilerDistanceSource } from './utils/mileageSource';
import { normalizeStopsForQuickQuoteSubmission } from './utils/normalizeStopsForQuickQuoteSubmission';
import { normalizeStopsFromLocationLine } from './utils/parseStopAddressFromLocation';
import { resolveNormalizedSubmitBidLoadId } from './utils/resolveNormalizedSubmitBidLoadId';
import { resolveTaiLtlPickupWindow } from './utils/resolveTaiLtlPickupWindow';
import { shouldShowQuickQuoteDateFields } from './utils/shouldShowQuickQuoteDateFields';

export enum FuelType {
  DOE = 'DOE',
  DAT = 'DAT',
  Portal = 'Portal', // e.g. Freightview, E2Open, etc.
  Kenco = 'Kenco', // Kenco 50/50 average fuel_cost_per_mile from quote metadata
  CustomerTable = 'CustomerTable', // Per-customer FSC table
  None = 'None', // Handles initialization logic
}

export type DistanceSource =
  Quoting | QuotingPortals | typeof PCMilerDistanceSource;

type QuickQuoteLoadIdentitySuggestion = {
  id: number;
  pipeline?: SuggestionPipelines;
  source?: QuotingPortals;
  sourceExternalID?: string;
  sourceURL?: string;
};

function getQuickQuoteLoadIdentity(
  suggestion: Maybe<QuickQuoteLoadIdentitySuggestion>
): string {
  if (!suggestion) {
    return '';
  }
  if (
    suggestion.pipeline === SuggestionPipelines.QuickQuote &&
    (suggestion.sourceExternalID || suggestion.sourceURL)
  ) {
    return `portal:${suggestion.source ?? 'unknown'}:${
      suggestion.sourceExternalID || suggestion.sourceURL
    }`;
  }
  return `id:${suggestion.id}`;
}

function getLTLDisplayLocation(stop: QuoteFormStop): string {
  const parsedLocation = parseLocation((stop.location ?? '').trim());
  const zip = (stop.zip || parsedLocation?.zip || '').trim();
  if (zip) {
    return zip;
  }

  const city = (stop.city || parsedLocation?.city || '').trim();
  const state = (stop.state || parsedLocation?.state || '').trim();
  if (city && state) {
    return `${city}, ${state}`;
  }

  return '';
}

function buildSubmitBidPortalOverrides(
  payload: SubmitBidPayload,
  quoteMode: QuoteMode,
  fuelEstimate: number
): SubmitPortalOverrides {
  const overrides: SubmitPortalOverrides = {
    selectedIntegrationName: payload.integrationName,
    credentialId: payload.credentialId,
    integrationId: payload.integrationId,
  };
  if (quoteMode === QuoteMode.TL) {
    overrides.fuelEstimate = fuelEstimate;
  }
  return overrides;
}

function createSubmitBidPortalInlineFeedback(
  setSubmitBidStatus: Dispatch<SetStateAction<Maybe<SubmitBidStatus>>>,
  setIsSubmitBidResultPending: (pending: boolean) => void
): PortalSubmitInlineFeedback {
  return {
    setUserMessage: (
      message,
      variant: SubmitBidStatusVariant = SubmitBidStatusVariant.Destructive
    ) => {
      setSubmitBidStatus(message ? { message, variant } : null);
    },
    setSuccessMessage: (message) => {
      setSubmitBidStatus(
        message
          ? {
              message,
              variant: SubmitBidStatusVariant.Success,
            }
          : null
      );
    },
    setIsSubmitBidResultPending,
  };
}

type SubmitQuickQuoteBidToPortalWithCaptureParams = {
  handleSubmitToPortal: (
    quote: Maybe<number>,
    quoteValueType: QuoteValueType,
    overrides?: SubmitPortalOverrides,
    inlineFeedback?: PortalSubmitInlineFeedback
  ) => Promise<void>;
  sellRate: number;
  quoteValueType: QuoteValueType;
  overrides: SubmitPortalOverrides;
  inlineFeedback: PortalSubmitInlineFeedback;
  setSubmitBidStatus: Dispatch<SetStateAction<Maybe<SubmitBidStatus>>>;
};

async function submitQuickQuoteBidToPortalWithCapture(
  params: SubmitQuickQuoteBidToPortalWithCaptureParams
): Promise<void> {
  try {
    await params.handleSubmitToPortal(
      params.sellRate,
      params.quoteValueType,
      params.overrides,
      params.inlineFeedback
    );
  } catch (error: unknown) {
    captureException(error, { functionName: 'handleSubmitBidSelection' });
    params.setSubmitBidStatus({
      message: SUBMIT_PORTAL_BID_USER_FACING_FAILURE_MESSAGE,
      variant: SubmitBidStatusVariant.Destructive,
    });
  }
}

export default function QuickQuoteForm({ email }: { email: Maybe<Email> }) {
  const { toast } = useToast();

  const {
    serviceFeaturesEnabled: {
      isGetLaneRateFromServiceEnabled,
      isMultiStopQuickQuoteEnabled,
      isQuoteLaneHistoryEnabled,
      isTMSLaneHistoryEnabled,
      isOnDemandDATLaneHistoryEnabled,
      isFTLQuickQuoteEnabled,
      isLTLQuickQuoteEnabled,
      isCubicFeetCustomCalculationEnabled,
      isBiddingPortalSubmitBidEnabled,
      isQuickQuoteCustomAccessorialsEnabled,
      isQuickQuoteSubTransportTypesEnabled,
      isQuickQuoteCoachEnabled,
    },
    tmsIntegrations,
    ltlTMSIntegrations,
    quotingIntegrations,
    quickQuoteConfig,
    subTransportTypes,
    serviceID,
    accessorialIntegrations,
  } = useServiceFeatures();
  const {
    isEnabled: isQuoteCurrencyToggleEnabled,
    formatAmount: formatQuoteAmount,
  } = useQuickQuoteCurrency();
  const [isKitCoachSelected, setIsKitCoachSelected] = useState(false);
  const kitCoachAutoSelectedForQuoteRef = useRef<Undef<number>>(undefined);

  const [isCheckingShiplifyForQuote, setIsCheckingShiplifyForQuote] =
    useState(false);

  const isThreeGActive = useMemo(() => {
    return ltlTMSIntegrations?.some(
      (integration) => integration.name === TMS.ThreeG
    );
  }, [ltlTMSIntegrations]);

  const isTurvoActive = useMemo(() => {
    return ltlTMSIntegrations?.some(
      (integration) => integration.name === TMS.Turvo
    );
  }, [ltlTMSIntegrations]);

  const isTaiActive = useMemo(() => {
    return ltlTMSIntegrations?.some(
      (integration) => integration.name === TMS.Tai
    );
  }, [ltlTMSIntegrations]);

  const activeLtlTmsIntegration = useMemo((): Undef<TMS> => {
    if (isThreeGActive) {
      return TMS.ThreeG;
    }

    if (isTurvoActive) {
      return TMS.Turvo;
    }

    if (isTaiActive) {
      return TMS.Tai;
    }

    return undefined;
  }, [isThreeGActive, isTurvoActive, isTaiActive]);

  // Tai-specific UI/submit gates must use the selected LTL TMS below, not `isTaiActive`.
  //
  // `isTaiActive` only indicates that Tai exists in `ltlTMSIntegrations`. When multiple
  // LTL TMS integrations are configured, `activeLtlTmsIntegration` selects a single
  // integration based on priority (ThreeG > Turvo > Tai), and that selection determines
  // which LTL form is rendered.
  //
  // Example: if both ThreeG and Tai are configured, `isTaiActive` will be true, but
  // ThreeG will be the active integration and its form will be rendered. In that case,
  // Tai-specific behavior (full-street prefill, validation, submission logic, etc.)
  // must remain disabled until Tai becomes the active integration.
  const isActiveTaiLtlIntegration = activeLtlTmsIntegration === TMS.Tai;
  const isActiveTurvoLtlIntegration = activeLtlTmsIntegration === TMS.Turvo;
  const ltlOutcomeConfig = useMemo(
    () => getLtlProviderOutcomeConfig(activeLtlTmsIntegration),
    [activeLtlTmsIntegration]
  );

  const shouldShowLaneHistoryTrends =
    isQuoteLaneHistoryEnabled || isTMSLaneHistoryEnabled;

  const {
    currentState: {
      clickedSuggestion,
      goToSuggestion,
      curSuggestionList,
      tabId,
      shouldKitAutoSelect,
    },
    setCurrentState,
  } = useContext(SidebarStateContext);

  const suggestedFields = clickedSuggestion?.suggested as QuoteChanges;
  const rawStops: Stop[] = normalizeToArray(suggestedFields?.stops) ?? [];
  const hasSuggestedMultiStops = rawStops.length > 2;
  const enableMultiStopUIFromSuggestion =
    isMultiStopQuickQuoteEnabled || hasSuggestedMultiStops;

  const {
    // Quote Response & Cards
    quoteResponse,
    setQuoteResponse,
    quoteCards,
    setQuoteCards,
    selectedQuote,
    setSelectedQuote,
    hasUserSelectedCard,
    setHasUserSelectedCard,
    quoteNotConfident,
    setQuoteNotConfident,
    // LTL State
    ltlQuotes,
    setLTLQuotes,
    selectedLTLQuotes,
    setSelectedLTLQuotes,
    isLoadingLTLQuotes,
    setIsLoadingLTLQuotes,
    ltlQuoteFetchError,
    setLtlQuoteFetchError,
    ltlCustomer,
    ltlCustomerStaff,
    ltlAccessorials,
    setLtlAccessorials,
    isLoadingAccessorials,
    setIsLoadingAccessorials,
    setLtlExternalQuoteURL,
    ltlExternalQuoteID,
    setLtlExternalQuoteID,
    ltlExternalQuoteSource,
    setLtlExternalQuoteSource,
    setLtlExternalQuoteLabel,
    setLtlExternalQuoteStatus,
    setLtlExternalQuoteErrorMessage,
    setLtlExternalQuoteAssignment,
    // Lane History State
    isLoadingLaneHistory,
    laneHistory,
    setLaneHistory,
    tmsLaneHistory,
    tmsLaneHistorySelectedTierIndex,
    setTMSLaneHistory,
    setTmsLaneHistorySelectedTierIndex,
    laneHistoryFromService,
    setLaneHistoryFromService,
    isLoadingLaneHistoryFromService,
    setIsLoadingLaneHistoryFromService,
    // Pricing State
    carrierCost,
    setCarrierCost,
    carrierCostType,
    setCarrierCostType,
    profit,
    setProfit,
    profitType,
    setProfitType,
    finalPrice,
    setFinalPrice,
    finalFlatPrice,
    setFinalFlatPrice,
    finalPriceFormat,
    setFinalPriceFormat,
    // Route & Distance State
    maxDistance,
    setMaxDistance,
    distanceSource,
    setDistanceSource,
    setRouteDetails,
    stopFee,
    setStopFee,
    intermediateStopCount,
    setIntermediateStopCount,
    lastAppliedStopFeeRef,
    // DAT State
    datFuelSurcharge,
    setDATFuelSurcharge,
    hasGrantedDATPermissions,
    setHasGrantedDATPermissions,
    isLoadingDAT,
    setIsLoadingDAT,
    // Draft Response State
    draftResponse,
    setDraftResponse,
    setHasCopiedDraftResponse,
    userEditedDraft,
    setUserEditedDraft,
    isBillRateEditSessionActive,
    // TMS & Customer State
    customers,
    tmsTenant,
    ensureCustomerByExternalTmsId,
    handleCustomerSearch,
    // Quote Submission State
    createdQuoteId,
    createdQuoteInternalId,
    parentQuoteRequestId,
    // UI State
    quoteMode,
    setQuoteMode,
    scrollResultsIntoViewRef,
    availableCustomAccessorials,
  } = useQuickQuoteContext();

  const kitCoachSuggestion = useMemo(
    () =>
      resolveLiveSuggestion({
        isQuickQuoteCoachEnabled,
        quotes: quoteResponse?.quotes,
      }),
    [isQuickQuoteCoachEnabled, quoteResponse?.quotes]
  );
  const showKitCoach = shouldShowCoach(
    isQuickQuoteCoachEnabled,
    quoteResponse?.quotes
  );

  const handleKitCoachSelect = useCallback(
    (suggestion: Suggestion) => {
      const applied = applySuggestion(suggestion);
      setIsKitCoachSelected(true);
      setSelectedQuote(null);
      setHasUserSelectedCard(true);
      setCarrierCostType(applied.carrierCostType);
      setCarrierCost(applied.carrierCost);
      setProfitType(applied.profitType);
      setProfit(applied.profit);
    },
    [
      setSelectedQuote,
      setHasUserSelectedCard,
      setCarrierCostType,
      setCarrierCost,
      setProfitType,
      setProfit,
    ]
  );

  useEffect(() => {
    if (!showKitCoach) {
      setIsKitCoachSelected(false);
      kitCoachAutoSelectedForQuoteRef.current = undefined;
    }
  }, [showKitCoach]);

  // Local state that's NOT in context
  const [fuelEstimate, setFuelEstimate] = useState(0);
  const [currentPriceString, setCurrentPriceString] = useState('');
  const [ltlQuoteReplyDraftTemplateBody, setLtlQuoteReplyDraftTemplateBody] =
    useState('');
  const [ltlInlineSystemFailureMessage, setLtlInlineSystemFailureMessage] =
    useState('');
  const [ltlInlineSystemFailureLevel, setLtlInlineSystemFailureLevel] =
    useState<'error' | 'warning'>('error');
  const [serviceUsesDAT, setServiceUsesDAT] = useState<boolean>(false);
  const [hasAddedQuoteDates, setHasAddedQuoteDates] = useState<boolean>(false);
  const [isSubmittingToPortal, setIsSubmittingToPortal] = useState(false);
  const [_resolvedSubmitBidUsesLinehaul, setResolvedSubmitBidUsesLinehaul] =
    useState<Maybe<boolean>>(null);
  const [submitBidStatus, setSubmitBidStatus] =
    useState<Maybe<SubmitBidStatus>>(null);
  const [isSubmitBidResultPending, setIsSubmitBidResultPending] =
    useState(false);
  const [
    submitBidResolvedBasisResetSignal,
    setSubmitBidResolvedBasisResetSignal,
  ] = useState(0);
  const [portal, setPortal] = useState<Undef<QuotingPortal>>();
  const [showSubmitToPortalButtons, setShowSubmitToPortalButtons] =
    useState<boolean>(false);
  const [portalInfo, setPortalInfo] = useState<Maybe<string>>(null);

  // For Quote Submission in TMS
  const [tmsName, setTMSName] = useState<Maybe<TMS>>(null);

  const ltlQuotePollTimerRef =
    useRef<Maybe<ReturnType<typeof setTimeout>>>(null);
  const ltlJobIdRef = useRef<Maybe<string>>(null);
  const [ltlZeroQuoteOutcome, setLtlZeroQuoteOutcome] =
    useState<Maybe<LtlZeroQuoteOutcome>>(null);
  const [ltlProviderFailureMessage, setLtlProviderFailureMessage] =
    useState<Maybe<string>>(null);
  const lastSuggestedCustomerFetchKeyRef = useRef<Maybe<string>>(null);
  const lastPortalCustomerPreselectKeyRef = useRef<Maybe<string>>(null);
  const lastPortalQuoteLoadSignatureRef = useRef<Maybe<string>>(null);
  // When true, skip clearing submit-bid UI when load id / portal deps change (e.g. mid-flight server submit + poll).
  const skipSubmitBidDerivedStateResetRef = useRef(false);
  // Set when the reset effect ran while `skipSubmitBidDerivedStateResetRef` was true, flushed in submit `finally`.
  // Do not clear `submitBidStatus` in that flush: `handleSubmitToPortal` may have just set success/error in the same tick.
  const pendingSubmitBidDerivedStateResetRef = useRef(false);
  // Count of overlapping `handleSubmitBidSelection` runs, spinner clears only when back to 0.
  const submitBidInflightCountRef = useRef(0);
  // Skip bumping `submitBidResolvedBasisResetSignal` on the first derived-reset effect run so
  // `SubmitBidSection` keeps its initial bid-rate resolution instead of wiping it on mount.
  const isFirstSubmitBidDerivedResetEffectRef = useRef(true);
  const customersRef = useRef(customers);
  const handleCustomerSearchRef = useRef(handleCustomerSearch);
  const rateBreakdownRef = useRef<Maybe<HTMLDivElement>>(null);

  useEffect(() => {
    customersRef.current = customers;
  }, [customers]);

  useEffect(() => {
    handleCustomerSearchRef.current = handleCustomerSearch;
  }, [handleCustomerSearch]);

  // Clear LTL poll timer on unmount so we don't update state after unmount.
  useEffect(() => {
    return () => {
      if (ltlQuotePollTimerRef.current) {
        clearTimeout(ltlQuotePollTimerRef.current);
        ltlQuotePollTimerRef.current = null;
      }
      ltlJobIdRef.current = null;
      setIsLoadingLTLQuotes(false);
      setLtlQuoteFetchError(false);
      setLtlZeroQuoteOutcome(null);
      setLtlProviderFailureMessage(null);
      setLtlInlineSystemFailureMessage('');
      setLtlInlineSystemFailureLevel('error');
    };
  }, [setIsLoadingLTLQuotes, setLtlQuoteFetchError]);

  useEffect(() => {
    // TODO: Support quote submission for multiple TMSes
    if (tmsIntegrations && tmsIntegrations.length > 0) {
      setTMSName(tmsIntegrations[0].name as TMS);
    }
  }, [tmsIntegrations]);

  const newCustomerURLInTMS = useMemo(
    () => getNewCustomerURLInTMS(tmsName, tmsTenant ?? null),
    [tmsName, tmsTenant]
  );

  const quoteURLInTMS = useMemo(() => {
    let quoteIdentifierForUrl: Maybe<string> = createdQuoteId;

    if (tmsName === TMS.ThreeG) {
      if (createdQuoteInternalId != null && createdQuoteInternalId > 0) {
        quoteIdentifierForUrl = createdQuoteInternalId.toString();
      } else {
        quoteIdentifierForUrl = null;
      }
    }

    if (!quoteIdentifierForUrl) {
      return null;
    }

    return getQuoteURLInTMS(quoteIdentifierForUrl, tmsName, tmsTenant ?? null);
  }, [createdQuoteId, createdQuoteInternalId, tmsName, tmsTenant]);

  const hasDATRateView = quoteCards.some(
    (card) => card.type === SelectedQuoteType.DAT_RATEVIEW
  );
  const showOnDemandDATLaneHistory =
    hasDATRateView && isOnDemandDATLaneHistoryEnabled;
  const hasTmsLaneHistoryResults = Boolean(
    laneHistory?.resultsBySource &&
    Object.entries(laneHistory.resultsBySource).some(
      ([source, histories]) =>
        Object.values(TMS).includes(source as TMS) && histories.length > 0
    )
  );
  const shouldShowNoTmsLaneHistoryMessage =
    isTMSLaneHistoryEnabled &&
    !isLoadingLaneHistory &&
    !hasTmsLaneHistoryResults;
  const hasLaneHistoryTrendsContent =
    shouldShowLaneHistoryTrends &&
    (isLoadingLaneHistory ||
      Boolean(
        laneHistory && Object.keys(laneHistory.resultsBySource ?? {}).length > 0
      ));
  const showLaneHistoryFromService =
    isGetLaneRateFromServiceEnabled &&
    (isLoadingLaneHistoryFromService || Boolean(laneHistoryFromService));
  const shouldShowLaneHistorySection =
    showOnDemandDATLaneHistory ||
    hasLaneHistoryTrendsContent ||
    showLaneHistoryFromService ||
    shouldShowNoTmsLaneHistoryMessage;
  const shouldShowProviderLtlFailure =
    Boolean(ltlOutcomeConfig) &&
    ltlZeroQuoteOutcome === LTL_ZERO_QUOTE_OUTCOME.ProviderFailure;
  const shouldShowEmptyLtlQuotesMessage =
    ltlJobIdRef.current != null &&
    !isLoadingLTLQuotes &&
    !ltlQuoteFetchError &&
    ltlQuotes.length === 0 &&
    (ltlOutcomeConfig
      ? ltlZeroQuoteOutcome === LTL_ZERO_QUOTE_OUTCOME.Empty
      : !shouldShowProviderLtlFailure);
  const shouldShowLtlRatesSection =
    quoteMode === QuoteMode.LTL &&
    (ltlQuotes.length > 0 ||
      isLoadingLTLQuotes ||
      ltlQuoteFetchError ||
      shouldShowProviderLtlFailure ||
      shouldShowEmptyLtlQuotesMessage);
  const ltlEmptyQuotesMessage =
    ltlOutcomeConfig?.emptyQuotesMessage ?? LTL_LEGACY_EMPTY_QUOTES_MESSAGE;
  const ltlSystemFailureMessage =
    ltlOutcomeConfig?.systemFailureMessage ?? LTL_LEGACY_SYSTEM_FAILURE_MESSAGE;

  const handleUpdateDATRate = async (
    timeframe: DATQuoteTimeframe,
    areaType: DATQuoteLocationType,
    setUpdateRateViewError: (error: Maybe<string>) => void
  ) => {
    setIsLoadingDAT(true);
    const formValues = getValues();

    if (quoteResponse == undefined || quoteResponse?.stops.length < 2) {
      toast({
        description: 'Insufficient stops for update',
        variant: 'destructive',
      });

      setIsLoadingDAT(false);
      return;
    }

    const origin = quoteResponse?.stops[0];
    const destination = quoteResponse?.stops[quoteResponse.stops.length - 1];
    const transportType = formValues.transportType;

    const request: UpdateLaneRateRequest = {
      originCity: origin.city,
      originState: origin.state,
      originZip: origin.zip,
      originCountry: QuoteCountries.USA,
      destinationCity: destination.city,
      destinationState: destination.state,
      destinationZip: destination.zip,
      destinationCountry: QuoteCountries.USA,
      transportType,
      specificTimeFrame: timeframe,
      specificAreaType: areaType,
    };

    const res = await updateRateView(request);

    if (!res.isOk()) {
      toast({
        description: 'Failed to update RateView',
        variant: 'destructive',
      });

      setUpdateRateViewError(res.error.message);

      setIsLoadingDAT(false);
      return;
    }

    const data = res.value;
    toast({
      description: 'RateView updated successfully',
      variant: 'success',
    });

    setQuoteCards((prev) =>
      prev.map((card) => {
        if (card.type !== SelectedQuoteType.DAT_RATEVIEW) {
          return card;
        }

        return {
          ...card,
          cost: data.rate.target,
          priceRange: {
            lowEstimate: data.rate.low,
            highEstimate: data.rate.high,
          },
          tooltipContent: card.tooltipContent
            ? {
                ...card.tooltipContent,
                companies: data.metadata?.companies,
                destinationType: areaType,
                originType: areaType,
                reports: data.metadata?.reports,
                timeframe: data.metadata?.timeframe,
              }
            : undefined,
        } as QuoteCardType;
      })
    );

    setDATFuelSurcharge(Number(data.rate.fuelSurchargePerMile));
    setIsLoadingDAT(false);
  };

  const hasSuperDispatchQuoting = useHasSuperDispatchQuoting();
  const transportTypeOptions = useMemo(
    () =>
      getTransportTypeOptions(quickQuoteConfig, {
        includeAuto: hasSuperDispatchQuoting,
      }),
    [quickQuoteConfig, hasSuperDispatchQuoting]
  );

  const subTransportTypeOptions = useMemo(
    () => getSubTransportTypeOptions(subTransportTypes),
    [subTransportTypes]
  );

  // Properties for GetQuickQuote button Posthog logging
  const getQuickQuoteProperties = useMemo(() => {
    return {
      serviceID,
      hasQRSuggestions: curSuggestionList.some(
        (s) => s.pipeline === SuggestionPipelines.QuickQuote
      ),
      profitType,
      carrierCostType,
      suggestionId: clickedSuggestion?.id,
      // Bound tab id only here (URL resolved at submit for FTL/LTL payloads).
      tabContext: {
        tabId: tabId || undefined,
      },
    };
  }, [
    serviceID,
    curSuggestionList,
    profitType,
    carrierCostType,
    clickedSuggestion,
    tabId,
  ]);

  // Properties for CopyQuoteToClipboard button Posthog logging
  const copyToClipboardProperties = useMemo(() => {
    if (!quoteResponse && !quoteNotConfident) return {};

    const { flatCarrierCost, finalProfit } = calculatePricing(
      carrierCost,
      carrierCostType,
      profit,
      profitType,
      maxDistance
    );

    return {
      serviceID,
      parentQuoteRequestId,
      finalQuotePrice: finalFlatPrice ?? 0,
      finalMargin: finalProfit,
      marginType: profitType,
      finalCarrierCost: flatCarrierCost,
      carrierCostType: carrierCostType,
      hasQRSuggestions: curSuggestionList.some(
        (s) => s.pipeline === SuggestionPipelines.QuickQuote
      ),
      suggestionId: clickedSuggestion?.id,
    };
  }, [
    quoteResponse,
    quoteNotConfident,
    carrierCost,
    carrierCostType,
    profit,
    profitType,
    maxDistance,
    serviceID,
    parentQuoteRequestId,
    finalFlatPrice,
    curSuggestionList,
    clickedSuggestion,
  ]);

  // Use effect for bidding portal logic when running as Chrome extension
  useEffect(() => {
    if (typeof chrome === 'undefined' || !chrome.runtime) {
      return;
    }

    let cancelled = false;
    let checkTabInFlight = false;

    const getSidebarAttachedTab = async (): Promise<Undef<chrome.tabs.Tab>> => {
      if (tabId && tabId > 0) {
        try {
          const tab = await chrome.tabs.get(tabId);
          if (tab?.id) {
            return tab;
          }
        } catch {
          return getCurrentTab();
        }
      }
      return getCurrentTab();
    };

    // Live document state from the tab (same source as bid-form visibility).
    const getLiveEmergeDocumentSnapshot = async (
      targetTabId: number
    ): Promise<
      Undef<{
        html: string;
        pathname: string;
        hostname: string;
        search: string;
      }>
    > => {
      try {
        const result = await chrome.scripting.executeScript({
          target: { tabId: targetTabId },
          func: () => {
            return {
              html: document.documentElement.outerHTML,
              pathname: window.location.pathname,
              hostname: window.location.hostname,
              search: window.location.search,
            };
          },
        });
        const value = result?.[0]?.result as
          | Undef<{
              html: string;
              pathname: string;
              hostname: string;
              search: string;
            }>
          | boolean;
        if (!value || typeof value === 'boolean') {
          return undefined;
        }
        return value;
      } catch {
        return undefined;
      }
    };

    let emergeMarketPopupPoll: Maybe<ReturnType<typeof setInterval>> = null;

    async function checkTab() {
      if (cancelled) {
        return;
      }

      if (checkTabInFlight) {
        return;
      }

      checkTabInFlight = true;

      try {
        const tab = await getSidebarAttachedTab();
        if (cancelled) return;

        if (!tab?.id) {
          if (emergeMarketPopupPoll) {
            clearInterval(emergeMarketPopupPoll);
            emergeMarketPopupPoll = null;
          }
          if (!cancelled) {
            setPortal(undefined);
            setPortalInfo(null);
            setShowSubmitToPortalButtons(false);
          }
          return;
        }

        const tabUrl = tab?.url ?? '';
        const parsedTabUrl = (() => {
          try {
            return new URL(tabUrl);
          } catch {
            return null;
          }
        })();

        let isEmergeSpotTab = false;
        let emergeSpotClosed = false;

        if (parsedTabUrl) {
          isEmergeSpotTab =
            isEmergeMarketHost(parsedTabUrl.hostname) &&
            isEmergeMarketSpotPath(parsedTabUrl.pathname);
          emergeSpotClosed = isEmergeMarketSpotClosedPath(
            parsedTabUrl.pathname
          );
        }

        if (cancelled) {
          return;
        }

        let portal = newQuotingPortal(tab?.url);

        if (cancelled) {
          return;
        }

        let html = '';
        let submittable = false;
        /** Tab URL aligned with `html` for Emerge when live snapshot exists (avoids SPA lag vs chrome.tabs URL). */
        let tabForAdditionalInfo: chrome.tabs.Tab = tab;

        if (portal) {
          if (isEmergeSpotTab) {
            const live = await getLiveEmergeDocumentSnapshot(tab.id);
            if (cancelled) {
              return;
            }

            if (live) {
              const livePageUrl = `https://${live.hostname}${live.pathname}${live.search}`;
              const portalFromLive = newQuotingPortal(livePageUrl);
              if (portalFromLive) {
                portal = portalFromLive;
              }

              isEmergeSpotTab =
                isEmergeMarketHost(live.hostname) &&
                isEmergeMarketSpotPath(live.pathname);
              emergeSpotClosed = isEmergeMarketSpotClosedPath(live.pathname);

              tabForAdditionalInfo = { ...tab, url: livePageUrl };
              html = live.html;
              submittable =
                emergeSpotBidFormPresentInHtml(live.html, live.pathname) &&
                !emergeSpotClosed;
            } else {
              html = (await getTabHTML(tab.id, portal.allFrames)) ?? '';

              if (cancelled) {
                return;
              }

              submittable = portal.canSubmit(tab, html);
            }
          } else {
            html = (await getTabHTML(tab.id, portal.allFrames)) ?? '';

            if (cancelled) {
              return;
            }

            submittable = portal.canSubmit(tab, html);
          }
        }

        if (cancelled) {
          return;
        }

        if (isEmergeSpotTab) {
          void chrome.runtime.sendMessage({
            action: 'emergemarket-ensure-listener',
            targetTabId: tab.id,
          });
        }

        setPortal(portal);

        const info = portal?.additionalInfo
          ? portal.additionalInfo(tabForAdditionalInfo, html)
          : null;

        if (cancelled) {
          return;
        }

        setPortalInfo(info);

        setShowSubmitToPortalButtons(!!portal && submittable);

        const shouldPollEmergeBidPopup = isEmergeSpotTab && !emergeSpotClosed;

        if (cancelled) {
          return;
        }

        if (shouldPollEmergeBidPopup) {
          if (!emergeMarketPopupPoll) {
            emergeMarketPopupPoll = setInterval(() => {
              void checkTab();
            }, 700);
          }
        } else if (emergeMarketPopupPoll) {
          clearInterval(emergeMarketPopupPoll);
          emergeMarketPopupPoll = null;
        }
      } catch (error) {
        captureException(error, {
          functionName: 'checkTab',
        });
      } finally {
        checkTabInFlight = false;
      }
    }

    checkTab();

    const listener = (message: any) => {
      if (!message) {
        return;
      }

      if (
        tabId &&
        message.targetTabId !== undefined &&
        message.targetTabId !== tabId
      ) {
        return;
      }

      if (message.action === UpdateQuoteRequestDataAction) {
        checkTab();
        return;
      }

      if (
        message.action === QuotingPortalPageStateUpdatedAction &&
        (message.portal === QuotingPortals.CarrierDesk ||
          message.portal === QuotingPortals.EmergeMarket ||
          message.portal === QuotingPortals.Manhattan ||
          message.portal === QuotingPortals.Kewill ||
          message.portal === QuotingPortals.Transporeon)
      ) {
        void checkTab();
      }
    };

    chrome.runtime.onMessage.addListener(listener);
    return () => {
      cancelled = true;
      if (emergeMarketPopupPoll) {
        clearInterval(emergeMarketPopupPoll);
        emergeMarketPopupPoll = null;
      }
      chrome.runtime.onMessage.removeListener(listener);
    };
  }, [tabId]);

  const fetchUserDATInfo = useCallback(async () => {
    const res = await getUserDATInfo();
    if (res.isOk()) {
      setHasGrantedDATPermissions(res.value.hasGrantedDATPermissions);
    }
  }, [setHasGrantedDATPermissions]);

  useEffect(() => {
    const serviceHasDATIntegration = quotingIntegrations.some(
      (qIntegration) => qIntegration.name === Quoting.DAT
    );

    if (serviceHasDATIntegration) {
      setServiceUsesDAT(true);
    }
  }, [quotingIntegrations]);

  useEffect(() => {
    if (serviceUsesDAT) {
      fetchUserDATInfo();
    }
  }, [serviceUsesDAT, fetchUserDATInfo]);

  const serverSubmitPortalNamesForQuoteMode = useMemo(
    () => getServerSubmitPortalNamesForQuoteMode(quoteMode),
    [quoteMode]
  );

  const defaultServerSubmitPortalName = useMemo(
    () =>
      resolveDefaultServerSubmitPortalName(
        serverSubmitPortalNamesForQuoteMode,
        email?.detectedBiddingPortal
      ),
    [serverSubmitPortalNamesForQuoteMode, email?.detectedBiddingPortal]
  );

  // Defaults follow the initial/detected portal only so changing the bidding-portal
  // dropdown does not rebuild defaults and reset the whole form.
  const defaultPortalLoadIdNormalizer = useMemo(
    () => getSubmitBidLoadIdNormalizer(defaultServerSubmitPortalName),
    [defaultServerSubmitPortalName]
  );
  const preserveInvalidPortalLoadId = shouldPreserveInvalidSubmitBidLoadId(
    defaultServerSubmitPortalName
  );

  const memoizedDefaultValues = useQuickQuoteDefaultValues(
    clickedSuggestion,
    quickQuoteConfig ?? null,
    enableMultiStopUIFromSuggestion,
    isTurvoActive,
    isActiveTaiLtlIntegration,
    isThreeGActive,
    email,
    defaultPortalLoadIdNormalizer,
    preserveInvalidPortalLoadId
  );

  const enableMultiStopUI =
    isMultiStopQuickQuoteEnabled || hasSuggestedMultiStops;

  const formMethods = useForm<QuickQuoteInputs>({
    defaultValues: memoizedDefaultValues,
    mode: 'onSubmit',
  });

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    setError,
    formState: { errors, isSubmitting, dirtyFields },
  } = formMethods;

  const submitBidLoadIdDirtyRef = useRef(false);
  submitBidLoadIdDirtyRef.current = dirtyFields.loadId === true;

  const { submitQuote, cancelSuperDispatchPoll } = useQuoteSubmission(
    formMethods,
    email
  );
  const { submitQuoteViaURL } = useQuoteSubmissionViaURL(formMethods, email);

  const { fields, append, remove } = useFieldArray<QuickQuoteInputs, 'stops'>({
    control,
    name: 'stops',
  });

  // Mohawk 3G: accessorials are customer-scoped - fetch only after a customer with ExternalID is selected.
  const customerNameWatched = useWatch({ control, name: 'customerName' });
  const customAccessorialsWatched = useWatch({
    control,
    name: 'customAccessorials',
  });
  const submitBidLoadIdWatched = useWatch({ control, name: 'loadId' });
  const customerNameForCredentialOptions =
    typeof customerNameWatched === 'string' && customerNameWatched.trim() !== ''
      ? customerNameWatched.trim()
      : undefined;

  useEffect(() => {
    const selectedCustomerId =
      typeof customerNameWatched === 'string' ? customerNameWatched.trim() : '';

    if (!selectedCustomerId) {
      return;
    }

    const hasSelectedCustomer = customers?.some(
      (customer) =>
        customer.externalTMSID != null &&
        String(customer.externalTMSID).trim() === selectedCustomerId
    );

    if (hasSelectedCustomer) {
      return;
    }

    void ensureCustomerByExternalTmsId(selectedCustomerId);
  }, [
    customerNameWatched,
    customers,
    ensureCustomerByExternalTmsId,
    quoteMode,
  ]);

  useEffect(() => {
    if (quoteMode !== QuoteMode.LTL || !isThreeGActive) {
      return;
    }

    if (!ltlCustomer?.externalId) {
      setLtlAccessorials([]);
      setIsLoadingAccessorials(false);
      return;
    }

    let cancelled = false;
    const customerExternalTmsId = ltlCustomer.externalId;

    const fetchAccessorials = async () => {
      setLtlAccessorials([]);
      setIsLoadingAccessorials(true);
      const result = await getCustomerLTLAccessorials({
        customerExternalTmsId,
      });

      if (cancelled) {
        return;
      }

      if (result.isOk()) {
        setLtlAccessorials(result.value);
      }

      setIsLoadingAccessorials(false);
    };

    void fetchAccessorials();

    return () => {
      cancelled = true;
      setLtlAccessorials([]);
      setIsLoadingAccessorials(false);
    };
  }, [
    quoteMode,
    isThreeGActive,
    ltlCustomer?.externalId,
    setIsLoadingAccessorials,
    setLtlAccessorials,
  ]);

  // Tai / Turvo / other LTL: unscoped list; load once when entering LTL if still empty.
  useEffect(() => {
    if (quoteMode !== QuoteMode.LTL || isThreeGActive) {
      return;
    }

    if (ltlAccessorials.length > 0) {
      return;
    }

    const fetchAccessorials = async () => {
      setIsLoadingAccessorials(true);

      const result = await getCustomerLTLAccessorials();
      if (result.isOk()) {
        setLtlAccessorials(result.value);
      }

      setIsLoadingAccessorials(false);
    };

    fetchAccessorials();
  }, [
    quoteMode,
    isThreeGActive,
    ltlAccessorials.length,
    setIsLoadingAccessorials,
    setLtlAccessorials,
  ]);

  const handleAddStop = () => {
    // Get current dropoff data before removing it
    const currentDropoff = getValues(`stops.${fields.length - 1}`);

    // Remove the dropoff stop
    remove(fields.length - 1);

    // Add the new intermediate stop
    append({
      order: fields.length,
      city: '',
      state: '',
      zip: '',
      country: QuoteCountries.USA,
      location: '',
    } as QuoteFormStop);

    // Add back the dropoff stop with preserved data
    append({
      order: fields.length,
      city: currentDropoff.city || '',
      state: currentDropoff.state || '',
      zip: currentDropoff.zip || '',
      country: currentDropoff.country || QuoteCountries.USA,
      location: currentDropoff.location || '',
    } as QuoteFormStop);

    // Focus on the new stop's location input
    setTimeout(() => {
      const newStopIndex = fields.length - 1; // The new stop is second to last
      const locationInput = document.querySelector(
        `input[name="stops.${newStopIndex}.location"]`
      ) as HTMLInputElement;
      locationInput?.focus();
    }, 100);
  };

  // Skip provider auto-select when Kit Coach is shown — otherwise this effect can
  // run in the same flush as Kit Coach auto-apply and overwrite carrierCost with DAT.
  useQuoteCardSelection(
    quoteCards,
    hasUserSelectedCard || showKitCoach,
    isTMSLaneHistoryEnabled,
    laneHistory,
    quickQuoteConfig?.defaultFTLBuyRateSource,
    setSelectedQuote,
    setCarrierCost
  );

  useEffect(() => {
    if (!kitCoachSuggestion || !quoteResponse?.quoteRequestId) {
      return;
    }
    if (
      kitCoachAutoSelectedForQuoteRef.current === quoteResponse.quoteRequestId
    ) {
      return;
    }
    kitCoachAutoSelectedForQuoteRef.current = quoteResponse.quoteRequestId;
    handleKitCoachSelect(kitCoachSuggestion);
  }, [kitCoachSuggestion, quoteResponse?.quoteRequestId, handleKitCoachSelect]);

  // Handle Kenco Flash defaults when the average card is selected
  useEffect(() => {
    if (
      quoteMode !== QuoteMode.TL ||
      selectedQuote !== SelectedQuoteType.KENCO_FLASH_AVERAGE
    ) {
      return;
    }

    const kencoQuote = quoteResponse?.quotes.find(
      (q) =>
        q.source === Quoting.KencoFlash &&
        q.type === QuoteTypeInSource.KencoAverage
    );
    if (!kencoQuote?.metadata) {
      return;
    }

    const marginPercentage = kencoQuote.metadata.average_margin_pct ?? 0;
    setProfitType(ProfitType.Percentage);
    setProfit(Math.round(marginPercentage * 100) / 100);
  }, [quoteMode, selectedQuote, quoteResponse, setProfit, setProfitType]);

  const kencoFuelSurcharge = useMemo((): Maybe<number> => {
    if (quoteMode !== QuoteMode.TL) {
      return null;
    }

    const kencoAverageQuote = quoteResponse?.quotes.find(
      (q) =>
        q.source === Quoting.KencoFlash &&
        q.type === QuoteTypeInSource.KencoAverage
    );
    if (!kencoAverageQuote?.metadata) {
      return null;
    }

    return kencoAverageQuote.metadata.fuel_cost_per_mile ?? null;
  }, [quoteMode, quoteResponse]);

  const customerTableFuelSurcharge = useMemo(
    (): Maybe<number> =>
      quoteResponse?.configuration?.customerTableFscPerMile ?? null,
    [quoteResponse?.configuration]
  );

  const getSelectedQuickQuoteId = (): Undef<number> => {
    return resolveSelectedQuickQuoteId({
      quotes: quoteResponse?.quotes,
      selectedQuote,
      isKitCoachSelected,
    });
  };

  const {
    handleTerminatingAction,
    handleSubmitQuoteToTMS,
    handleDraftResponse,
    handleSendResponse,
    handleSubmitToPortal,
  } = useQuoteHandlers(
    email,
    getValues,
    setError,
    getSelectedQuickQuoteId,
    portal,
    setIsSubmittingToPortal,
    quickQuoteConfig ?? null,
    submitBidLoadIdDirtyRef
  );

  const resolvedQuoteRequestId =
    parentQuoteRequestId ?? clickedSuggestion?.id ?? 0;
  useQuoteConfirmationPing(
    resolvedQuoteRequestId,
    getValues,
    getSelectedQuickQuoteId
  );

  const [DATLaneHistoryData, setDATLaneHistoryData] =
    useState<Maybe<DATLaneHistory>>(null);
  const [hasPulledDATLaneHistory, setHasPulledDATLaneHistory] =
    useState<boolean>(false);

  const isSubmitBidFeatureEnabled = isBiddingPortalSubmitBidEnabled;
  const canShowSubmitBidForQuoteMode =
    isSubmitBidFeatureEnabled && serverSubmitPortalNamesForQuoteMode.length > 0;
  const hasBidPricingForCurrentMode =
    quoteMode === QuoteMode.TL
      ? quoteResponse != null
      : selectedLTLQuotes.length > 0;

  const submitBidThreadEligible = isSubmitBidThreadEligible(email);
  // Auto-bid-eligible detection defaults to Submit Bid; empty detection and
  // attribution-only mail default to Draft Email.
  const defaultBidFlowMode = shouldDefaultToSubmitBidFlow(email)
    ? BidFlowMode.SubmitBid
    : BidFlowMode.DraftEmail;

  const submitBidContextKey = `${email?.id ?? 'no-email'}:${
    clickedSuggestion?.id ?? 'no-suggestion'
  }`;
  const [serverSubmitPortalSelection, setServerSubmitPortalSelection] =
    useState<
      Maybe<{
        contextKey: string;
        portalName: Maybe<string>;
      }>
    >(null);
  const selectedServerSubmitPortalName =
    serverSubmitPortalSelection?.contextKey === submitBidContextKey
      ? serverSubmitPortalSelection.portalName
      : null;
  const handleSelectedServerSubmitPortalChange = useCallback(
    (portalName: Maybe<string>) => {
      setServerSubmitPortalSelection({
        contextKey: submitBidContextKey,
        portalName,
      });
    },
    [submitBidContextKey]
  );
  const activeServerSubmitPortalName =
    selectedServerSubmitPortalName ?? defaultServerSubmitPortalName;
  const submitBidLoadIdNormalizer = useMemo(
    () => getSubmitBidLoadIdNormalizer(activeServerSubmitPortalName),
    [activeServerSubmitPortalName]
  );
  const normalizedSubmitBidLoadId = useMemo(
    () =>
      resolveNormalizedSubmitBidLoadId(
        typeof submitBidLoadIdWatched === 'string'
          ? submitBidLoadIdWatched
          : '',
        email,
        {
          inferEmailWhenFormLoadIdEmpty: dirtyFields.loadId !== true,
          normalizeLoadId: submitBidLoadIdNormalizer,
        }
      ),
    [
      submitBidLoadIdWatched,
      email,
      dirtyFields.loadId,
      submitBidLoadIdNormalizer,
    ]
  );

  const serverSubmitBidCredentialFetchEnabled =
    isSubmitBidFeatureEnabled &&
    canShowSubmitBidForQuoteMode &&
    hasBidPricingForCurrentMode &&
    submitBidThreadEligible;

  const { credentialOptionsByPortal, isPortalCredentialBatchLoading } =
    usePortalCredentialOptionsBatch({
      enabled: serverSubmitBidCredentialFetchEnabled,
      portals: serverSubmitPortalNamesForQuoteMode,
      tmsCustomerId: (clickedSuggestion?.suggested as Undef<QuoteChanges>)
        ?.customerExternalTMSID,
      customerName: customerNameForCredentialOptions,
    });

  const hasServerSubmitCredentials = serverSubmitPortalNamesForQuoteMode.some(
    (portalName) =>
      (credentialOptionsByPortal[portalName]?.eligibleCredentials.length ?? 0) >
      0
  );

  const bidFlowEligibleBase =
    isSubmitBidFeatureEnabled &&
    canShowSubmitBidForQuoteMode &&
    hasBidPricingForCurrentMode &&
    hasServerSubmitCredentials &&
    submitBidThreadEligible &&
    !isPortalCredentialBatchLoading;

  const [bidFlowMode, setBidFlowMode] = useState<BidFlowMode>(
    BidFlowMode.DraftEmail
  );
  const bidFlowContextRef = useRef<Undef<BidFlowCommittedContext>>(undefined);

  const clickedSuggestionId = clickedSuggestion?.id;
  const bidFlowEmailKey = `${email?.id ?? 'no-email'}`;
  const bidFlowThreadKey = email?.threadID;

  // apply-default on email / suggestion A->B; hydrate commits id without reset.
  // Skips when eligibility dips (cred loading) so manual Draft/Submit survives flicker.
  useEffect(() => {
    if (!bidFlowEligibleBase) {
      return;
    }

    const decision = decideBidFlowContextReset(
      bidFlowContextRef.current,
      bidFlowEmailKey,
      clickedSuggestionId,
      bidFlowThreadKey
    );
    bidFlowContextRef.current = decision.next;

    if (decision.action === 'apply-default') {
      setBidFlowMode(defaultBidFlowMode);
    }
  }, [
    bidFlowEligibleBase,
    bidFlowEmailKey,
    bidFlowThreadKey,
    clickedSuggestionId,
    defaultBidFlowMode,
  ]);

  // Hide draft-email UI on bidding portals; Submit/Input stay in QuoteSubmissionSection.
  const isOnQuotingPortal = useIsOnQuotingPortal();

  const shouldShowSubmitBidSection =
    bidFlowEligibleBase &&
    (isOnQuotingPortal || bidFlowMode === BidFlowMode.SubmitBid);

  const shouldShowBottomDraftResponse =
    !isOnQuotingPortal &&
    (!bidFlowEligibleBase || bidFlowMode === BidFlowMode.DraftEmail);

  useEffect(() => {
    if (skipSubmitBidDerivedStateResetRef.current) {
      pendingSubmitBidDerivedStateResetRef.current = true;
      return;
    }
    pendingSubmitBidDerivedStateResetRef.current = false;
    setSubmitBidStatus(null);
    setIsSubmitBidResultPending(false);
    setResolvedSubmitBidUsesLinehaul(null);
    if (isFirstSubmitBidDerivedResetEffectRef.current) {
      isFirstSubmitBidDerivedResetEffectRef.current = false;
    } else {
      setSubmitBidResolvedBasisResetSignal((n) => n + 1);
    }
  }, [
    activeServerSubmitPortalName,
    normalizedSubmitBidLoadId,
    bidFlowMode,
    clickedSuggestion?.id,
  ]);

  const handleSubmitBidSelection = async (payload: SubmitBidPayload) => {
    // Same UX as LTL Get quote: keep the portal submit spinner through every
    // async step (bid-rate resolution + server submit), not only inside
    // handleSubmitToPortal (react-hook-form isSubmitting is N/A for type="button").
    if (submitBidInflightCountRef.current > 0) {
      return;
    }

    submitBidInflightCountRef.current += 1;
    skipSubmitBidDerivedStateResetRef.current = true;
    setSubmitBidStatus(null);
    setIsSubmitBidResultPending(false);
    setIsSubmittingToPortal(true);
    try {
      const usesLinehaul = payload.effectiveSubmitBidUsesLinehaul;

      const submitPortalOverrides = buildSubmitBidPortalOverrides(
        payload,
        quoteMode,
        fuelEstimate
      );
      const portalSubmitInlineFeedback = createSubmitBidPortalInlineFeedback(
        setSubmitBidStatus,
        setIsSubmitBidResultPending
      );
      const quoteValueType = usesLinehaul
        ? QuoteValueType.Linehaul
        : QuoteValueType.TotalSell;

      await submitQuickQuoteBidToPortalWithCapture({
        handleSubmitToPortal,
        sellRate: payload.sellRate,
        quoteValueType,
        overrides: submitPortalOverrides,
        inlineFeedback: portalSubmitInlineFeedback,
        setSubmitBidStatus,
      });
    } finally {
      skipSubmitBidDerivedStateResetRef.current = false;
      submitBidInflightCountRef.current -= 1;
      if (
        submitBidInflightCountRef.current <= 0 &&
        pendingSubmitBidDerivedStateResetRef.current
      ) {
        pendingSubmitBidDerivedStateResetRef.current = false;
        // Only reset derived linehaul + poll UI. Omit `setSubmitBidStatus(null)` so a terminal
        // success/error from the submit handler is not wiped when deps changed mid-flight.
        setIsSubmitBidResultPending(false);
        setResolvedSubmitBidUsesLinehaul(null);
        setSubmitBidResolvedBasisResetSignal((n) => n + 1);
      }
      if (submitBidInflightCountRef.current <= 0) {
        submitBidInflightCountRef.current = 0;
        setIsSubmittingToPortal(false);
      }
    }
  };

  const resetQuoteState = useCallback(() => {
    if (ltlQuotePollTimerRef.current) {
      clearTimeout(ltlQuotePollTimerRef.current);
      ltlQuotePollTimerRef.current = null;
    }
    ltlJobIdRef.current = null;
    // submitQuote stops polling only after validation passes. If validation
    // fails, keep the previous card and its poll intact.

    setCarrierCostType(CarrierCostType.Flat);
    setLaneHistory(null);
    setLaneHistoryFromService(null);
    setDraftResponse('');
    setUserEditedDraft(false);
    setFinalPrice(null);
    setFinalPriceFormat(CarrierCostType.Flat);
    setDATLaneHistoryData(null);
    setHasPulledDATLaneHistory(false);
    setHasUserSelectedCard(false);
    setIsKitCoachSelected(false);
    kitCoachAutoSelectedForQuoteRef.current = undefined;
    setLTLQuotes([]);
    setSelectedLTLQuotes([]);
    setIsLoadingLTLQuotes(false);
    setLtlQuoteFetchError(false);
    setLtlZeroQuoteOutcome(null);
    setLtlProviderFailureMessage(null);
    setLtlInlineSystemFailureMessage('');
    setLtlInlineSystemFailureLevel('error');
    setLtlExternalQuoteURL(null);
    setLtlExternalQuoteID(null);
    setLtlExternalQuoteSource(null);
    setLtlExternalQuoteLabel(null);
    setLtlExternalQuoteStatus(null);
    setLtlExternalQuoteErrorMessage(null);
    setLtlExternalQuoteAssignment(null);
    setLtlQuoteReplyDraftTemplateBody('');
    setMaxDistance(0);
    setDistanceSource(null);
  }, [
    setCarrierCostType,
    setDATLaneHistoryData,
    setDistanceSource,
    setDraftResponse,
    setFinalPrice,
    setFinalPriceFormat,
    setHasPulledDATLaneHistory,
    setHasUserSelectedCard,
    setIsKitCoachSelected,
    setIsLoadingLTLQuotes,
    setLaneHistory,
    setLaneHistoryFromService,
    setLTLQuotes,
    setLtlExternalQuoteAssignment,
    setLtlExternalQuoteID,
    setLtlExternalQuoteLabel,
    setLtlExternalQuoteSource,
    setLtlExternalQuoteStatus,
    setLtlExternalQuoteErrorMessage,
    setLtlExternalQuoteURL,
    setLtlInlineSystemFailureLevel,
    setLtlInlineSystemFailureMessage,
    setLtlProviderFailureMessage,
    setLtlQuoteFetchError,
    setLtlQuoteReplyDraftTemplateBody,
    setLtlZeroQuoteOutcome,
    setMaxDistance,
    setSelectedLTLQuotes,
    setUserEditedDraft,
  ]);

  const onSubmitFTL = async (formValues: QuickQuoteInputs) => {
    // Check for ZIP+4 inputs and convert to regular ZIP if necessary
    convertZipPlus4ToRegular({ formValues, setValue });

    const currentSelectedQuickQuoteId = getSelectedQuickQuoteId();

    try {
      await submitQuote(formValues, currentSelectedQuickQuoteId);
    } catch (error) {
      captureException(error, {
        functionName: 'onSubmitFTL',
      });
    }

    // Fetch lane history from service if enabled - will be called after quote is available
    if (isGetLaneRateFromServiceEnabled) {
      setIsLoadingLaneHistoryFromService(true);
    }
  };

  const onSubmitLTL: SubmitHandler<QuickQuoteInputs> = async (formValues) => {
    if (!ltlCustomer?.customerId) {
      setError('customerName', { message: 'Select a customer' });
      toast({
        description: 'Select a customer before requesting LTL rates.',
        variant: 'destructive',
      });
      return;
    }

    const validationErrors = validateLTLForm(formValues, setError, {
      acceptFullStreetLocations: isActiveTaiLtlIntegration,
      validateFreightClassAgainstDropdown:
        isActiveTaiLtlIntegration || isActiveTurvoLtlIntegration,
      validateTaiPickupDeliveryWindows: isActiveTaiLtlIntegration,
    });

    if (
      !isThreeGActive &&
      !isTurvoActive &&
      (!ltlCustomerStaff?.ownerId || !ltlCustomerStaff?.ownerName)
    ) {
      setError('customerName', { message: 'Select customer staff' });
      toast({
        description: 'Select customer staff before requesting LTL rates.',
        variant: 'destructive',
      });
      return;
    }

    if (validationErrors.length > 0) {
      const hasCustomerError = validationErrors.some(([field]) =>
        field.includes('customerName')
      );

      const hasStopError = validationErrors.some(([field]) =>
        field.includes('stops')
      );

      const hasItemError = validationErrors.some(([field]) =>
        field.includes('ltl.items')
      );

      const hasAccessorialError = validationErrors.some(([field]) =>
        field.includes('selectedAccessorials')
      );

      const hasDateWindowError = validationErrors.some(
        ([field]) =>
          field === 'pickupDate' ||
          field === 'pickupCloseDate' ||
          field === 'deliveryDate' ||
          field === 'deliveryCloseDate'
      );

      const errorParts: string[] = [];

      if (hasCustomerError) {
        errorParts.push('Select a customer');
      }

      if (hasStopError) {
        errorParts.push('Enter valid locations');
      }

      if (hasItemError) {
        errorParts.push('Complete item details');
      }

      if (hasAccessorialError) {
        errorParts.push('Fill in accessorial fields');
      }

      if (hasDateWindowError) {
        errorParts.push('Pickup/Dropoff dates and times');
      }

      const errorMessage =
        errorParts.length > 0
          ? `Please fix the following errors: ${errorParts.join(', ')}`
          : 'Please fix the validation errors in the form.';

      // Show a compact message in the inline root error banner
      setError('root', { message: errorMessage });

      // Also surface the same message in a toast, without extra layout-affecting markup
      toast({
        description: errorMessage,
        variant: 'destructive',
      });

      return;
    }

    // Check for ZIP+4 inputs and convert to regular ZIP if necessary
    convertZipPlus4ToRegular({ formValues, setValue });
    const normalizedStops = isActiveTaiLtlIntegration
      ? normalizeStopsFromLocationLine(formValues.stops)
      : normalizeStopsForQuickQuoteSubmission(formValues.stops);

    let normalizedFormValues: QuickQuoteInputs = {
      ...formValues,
      stops: normalizedStops,
    };

    setIsLoadingLTLQuotes(true);
    const shouldCheckShiplify =
      isActiveTaiLtlIntegration &&
      accessorialIntegrations.some(
        (integration) => integration.name === AccessorialIntegration.Shiplify
      );
    if (shouldCheckShiplify) {
      setIsCheckingShiplifyForQuote(true);

      const shiplifyAddressKeysAtStart = getShiplifyAddressKeys(
        normalizedFormValues.stops
      );

      const skipAddressBookStops =
        quickQuoteConfig?.skipShiplifyForAddressBookStops === true;
      const shiplifyStopsToCheck = getShiplifyStopsToCheck(
        normalizedFormValues.stops,
        normalizedFormValues.ltl?.shiplifyLookups ?? {},
        {
          requireLocationLineFullAddress: true,
          skipAddressBookStops,
        }
      );

      if (shiplifyStopsToCheck.length > 0) {
        const stopLabel =
          shiplifyStopsToCheck.length === 2
            ? 'pickup and dropoff'
            : shiplifyStopsToCheck[0];
        toast({
          description: `We're checking Shiplify for ${stopLabel} before requesting rates.`,
          variant: 'info',
          duration: 3000,
        });
      }

      try {
        const lookedUpFormValues = await applyMissingShiplifyLookups(
          normalizedFormValues,
          ltlAccessorials,
          {
            requireLocationLineFullAddress: true,
            skipAddressBookStops,
          }
        );

        const currentShiplifyAddressKeys = getShiplifyAddressKeys(
          getValues('stops')
        );

        const addressChanged = SHIPLIFY_STOP_TYPES.some(
          (stopType) =>
            shiplifyAddressKeysAtStart[stopType] !==
            currentShiplifyAddressKeys[stopType]
        );

        if (addressChanged) {
          toast({
            description:
              'Destination changed while checking Shiplify. Please request rates again.',
            variant: 'warning',
          });

          setIsLoadingLTLQuotes(false);
          return;
        }

        normalizedFormValues = lookedUpFormValues;

        setValue(
          'ltl.selectedAccessorials',
          normalizedFormValues.ltl?.selectedAccessorials ?? [],
          { shouldDirty: true }
        );
        setValue(
          'ltl.shiplifyLookups',
          normalizedFormValues.ltl?.shiplifyLookups ?? {},
          { shouldDirty: true }
        );
      } catch (error) {
        captureException(error, {
          functionName: 'applyMissingShiplifyLookups',
        });
        toast({
          description: 'Could not check Shiplify. Requesting rates without it.',
          variant: 'warning',
        });
      } finally {
        setIsCheckingShiplifyForQuote(false);
      }
    }
    setLtlQuoteFetchError(false);
    setLtlZeroQuoteOutcome(null);
    setLtlProviderFailureMessage(null);
    setLtlInlineSystemFailureMessage('');
    setLtlInlineSystemFailureLevel('error');
    setLtlExternalQuoteURL(null);
    setLtlExternalQuoteID(null);
    setLtlExternalQuoteSource(null);
    setLtlExternalQuoteLabel(null);
    setLtlExternalQuoteStatus(null);
    setLtlExternalQuoteErrorMessage(null);
    setLtlExternalQuoteAssignment(null);
    const cubicFeetCalculationConfig =
      isCubicFeetCustomCalculationEnabled &&
      quickQuoteConfig?.cubicFeetCalculationConfig &&
      quickQuoteConfig.cubicFeetCalculationConfig.heightThresholdInches > 0 &&
      quickQuoteConfig.cubicFeetCalculationConfig.overrideHeightInches > 0
        ? {
            heightThresholdInches:
              quickQuoteConfig.cubicFeetCalculationConfig.heightThresholdInches,
            overrideHeightInches:
              quickQuoteConfig.cubicFeetCalculationConfig.overrideHeightInches,
          }
        : undefined;
    const ltlQuoteRequestId =
      parentQuoteRequestId || clickedSuggestion?.id || 0;

    let request;
    if (isThreeGActive) {
      request = buildLTLThreeGRequest(
        normalizedFormValues,
        ltlCustomer,
        ltlCustomerStaff,
        ltlQuoteRequestId,
        email?.id ?? 0,
        email?.threadID ?? '',
        ltlAccessorials,
        {
          hideConstraintSection: Boolean(
            quickQuoteConfig?.hideConstraintSection
          ),
        }
      );
    } else if (isTurvoActive) {
      request = buildLTLTurvoRequest(
        normalizedFormValues,
        ltlCustomer,
        ltlQuoteRequestId,
        email?.id ?? 0,
        email?.threadID ?? ''
      );
    } else {
      request = buildLTLTaiRequest(
        normalizedFormValues,
        ltlCustomer,
        ltlCustomerStaff,
        ltlQuoteRequestId,
        email?.id ?? 0,
        email?.threadID ?? '',
        cubicFeetCalculationConfig,
        isActiveTaiLtlIntegration
      );
    }

    let tabURL: Undef<string>;
    if (tabId && tabId > 0) {
      try {
        const tab = await chrome.tabs.get(tabId);
        tabURL = tab.url;
      } catch {
        // Tab may be closed; omit portal source context.
      }
    }
    const sourceContext = resolveQuickQuoteSourceContext({
      tabURL,
      hasEmail: Boolean(email?.id),
    });
    if (sourceContext) {
      request = { ...request, ...sourceContext };
    }

    const startResult = await getLTLQuickQuotes(request as any);

    if (startResult.isErr()) {
      setIsLoadingLTLQuotes(false);
      setLtlQuoteFetchError(true);
      const isWarning = startResult.error.level === 'warning';
      const toastDescription =
        startResult.error.message || 'Failed to fetch LTL quotes';
      setLtlInlineSystemFailureMessage(toastDescription);
      setLtlInlineSystemFailureLevel(isWarning ? 'warning' : 'error');
      toast({
        description: toastDescription,
        variant: isWarning ? 'warning' : 'destructive',
      });
      return;
    }

    const jobId = startResult.value.jobId;
    ltlJobIdRef.current = jobId;

    if (ltlQuotePollTimerRef.current) {
      clearTimeout(ltlQuotePollTimerRef.current);
      ltlQuotePollTimerRef.current = null;
    }

    const maxPollAttempts = getLtlQuotePollMaxAttempts(activeLtlTmsIntegration);
    const outcomeConfig = getLtlProviderOutcomeConfig(activeLtlTmsIntegration);
    let pollAttempt = 0;
    // Poll status: first call after initial delay, then follow the schedule until completion.
    const applyLTLQuoteResult = (value: LTLQuickQuoteResponse) => {
      const result = classifyLTLQuoteResult(value, {
        isThreeGActive,
        outcomeConfig,
      });

      setLTLQuotes(result.quotes);
      setLtlProviderFailureMessage(result.providerFailureMessage);
      setLtlZeroQuoteOutcome(result.zeroQuoteOutcome);

      setLtlQuoteFetchError(false);
      setLtlInlineSystemFailureMessage('');
      setLtlInlineSystemFailureLevel('error');

      setLtlExternalQuoteURL(result.externalQuoteURL);
      setLtlExternalQuoteID(result.externalQuoteID);
      setLtlExternalQuoteSource(result.externalQuoteSource);
      setLtlExternalQuoteLabel(result.externalQuoteLabel);
      setLtlExternalQuoteAssignment(result.externalQuoteAssignment);
      setLtlExternalQuoteErrorMessage(result.externalQuoteErrorMessage);
      setLtlQuoteReplyDraftTemplateBody(result.templateBody);
      setLtlExternalQuoteStatus(result.externalQuoteStatus);

      if (result.defaultSelectedQuotes.length > 0) {
        setSelectedLTLQuotes(result.defaultSelectedQuotes);
      }
    };

    const runPoll = async () => {
      if (ltlJobIdRef.current !== jobId) {
        return;
      }

      pollAttempt += 1;
      const statusResult = await getLTLQuickQuoteResult({
        jobId,
        stops: request.stops,
      });

      if (ltlJobIdRef.current !== jobId) {
        return;
      }

      if (statusResult.isErr()) {
        const status = statusResult.error.status;
        const isRetryable = status === 202 || status === 404;

        if (!isRetryable || pollAttempt >= maxPollAttempts) {
          setIsLoadingLTLQuotes(false);
          setLtlQuoteFetchError(true);
          setLtlZeroQuoteOutcome(null);
          setLtlProviderFailureMessage(null);

          const shouldUseTimeoutFallback =
            isRetryable && pollAttempt >= maxPollAttempts;
          const isWarning = statusResult.error.level === 'warning';

          const toastDescription = shouldUseTimeoutFallback
            ? (outcomeConfig?.systemFailureMessage ??
              LTL_LEGACY_SYSTEM_FAILURE_MESSAGE)
            : statusResult.error.message || LTL_LEGACY_SYSTEM_FAILURE_MESSAGE;
          let toastVariant: 'destructive' | 'warning';
          if (shouldUseTimeoutFallback) {
            toastVariant = 'destructive';
          } else if (isWarning) {
            toastVariant = 'warning';
          } else {
            toastVariant = 'destructive';
          }
          setLtlInlineSystemFailureMessage(toastDescription);
          setLtlInlineSystemFailureLevel(
            !shouldUseTimeoutFallback && isWarning ? 'warning' : 'error'
          );

          toast({
            description: toastDescription,
            variant: toastVariant,
          });

          ltlQuotePollTimerRef.current = null;

          return;
        }

        const nextInterval = getLtlQuotePollIntervalMs(
          pollAttempt,
          activeLtlTmsIntegration
        );
        ltlQuotePollTimerRef.current = setTimeout(runPoll, nextInterval);

        return;
      }

      ltlQuotePollTimerRef.current = null;
      setIsLoadingLTLQuotes(false);
      applyLTLQuoteResult(statusResult.value);
    };

    // First attempt after the initial schedule delay
    ltlQuotePollTimerRef.current = setTimeout(
      runPoll,
      getLtlQuotePollIntervalMs(0, activeLtlTmsIntegration)
    );
  };

  const handleSelectLTLQuote = (quote: LTLQuote) => {
    setSelectedLTLQuotes((prev) => {
      const quoteIdentifier = getLTLQuoteIdentity(quote);
      const isAlreadySelected = prev.some(
        (q) => getLTLQuoteIdentity(q) === quoteIdentifier
      );

      if (isAlreadySelected) {
        return prev.filter((q) => getLTLQuoteIdentity(q) !== quoteIdentifier);
      }

      return [...prev, quote];
    });
    setUserEditedDraft(false);
  };

  const onInvalidForm: SubmitErrorHandler<QuickQuoteInputs> = (errors) => {
    handleFormErrors(errors, {
      activeTabs: [],
      setActiveTabs: () => {},
      toast,
      maxLabelsToShow: 6,
      tmsName:
        quoteMode === QuoteMode.LTL && isActiveTaiLtlIntegration
          ? TMS.Tai
          : undefined,
    });
  };

  const onSubmitForm: SubmitHandler<QuickQuoteInputs> = async (formValues) => {
    // Reset state variables for new quote
    resetQuoteState();

    if (quoteMode === QuoteMode.LTL) {
      await onSubmitLTL(formValues);
    } else {
      await onSubmitFTL(formValues);
    }
  };

  // Default TMS Lane History Tier hierarchy: Market-to-Market -> 3-Digit Zip -> first available tier
  useEffect(() => {
    const tmsHistoriesKey = Object.keys(
      laneHistory?.resultsBySource || {}
    ).find((source) => Object.values(TMS).includes(source as TMS));

    if (!tmsHistoriesKey) {
      setTMSLaneHistory(null);
      setTmsLaneHistorySelectedTierIndex(null);
      return;
    }

    const laneHistoryResults =
      laneHistory?.resultsBySource[tmsHistoriesKey as TMS];
    setTMSLaneHistory(laneHistoryResults || null);

    setTmsLaneHistorySelectedTierIndex(
      laneHistoryResults?.length
        ? getDefaultLaneHistoryTierIndex(laneHistoryResults)
        : 0
    );
  }, [laneHistory, setTMSLaneHistory, setTmsLaneHistorySelectedTierIndex]);

  // Fetch lane history from service when quote becomes available
  // TODO: Multi-stop for Trident ENG-3993
  useEffect(() => {
    if (
      isGetLaneRateFromServiceEnabled &&
      quoteResponse &&
      isLoadingLaneHistoryFromService
    ) {
      const fetchLaneHistoryData = async () => {
        const lastIndex = quoteResponse.stops.length - 1;
        try {
          const laneHistoryRes = await fetchLaneHistoryFromService({
            quoteRequestId: quoteResponse.quoteRequestId,
            originCity: quoteResponse.stops[0].city,
            originState: quoteResponse.stops[0].state,
            originZip: quoteResponse.stops[0].zip,
            originCountry: quoteResponse.stops[0].country ?? '',
            destinationCity: quoteResponse.stops[lastIndex].city,
            destinationState: quoteResponse.stops[lastIndex].state,
            destinationZip: quoteResponse.stops[lastIndex].zip,
            destinationCountry: quoteResponse.stops[lastIndex].country ?? '',
            transportType: getValues('transportType'),
          });
          if (laneHistoryRes.isOk()) {
            setLaneHistoryFromService(laneHistoryRes.value);
          }
        } catch (error) {
          captureException(error, {
            functionName: 'fetchLaneHistoryFromService',
          });
        } finally {
          setIsLoadingLaneHistoryFromService(false);
        }
      };

      fetchLaneHistoryData();
    }
  }, [
    isGetLaneRateFromServiceEnabled,
    quoteResponse,
    isLoadingLaneHistoryFromService,
    getValues,
    setIsLoadingLaneHistoryFromService,
    setLaneHistoryFromService,
  ]);

  const toggleLaneHistoryGraph = (option: string) => {
    if (!tmsLaneHistory) {
      return;
    }

    const tierIndex = tmsLaneHistory?.findIndex(
      (history) => history.laneTier === option
    );

    if (tierIndex === -1) {
      return;
    }

    setTmsLaneHistorySelectedTierIndex(tierIndex);
  };

  useEffect(() => {
    if (
      tmsLaneHistorySelectedTierIndex === null ||
      !tmsLaneHistory?.length ||
      tmsLaneHistorySelectedTierIndex >= tmsLaneHistory.length
    ) {
      return;
    }
    const tier = getLaneHistoryTierDisplay(
      tmsLaneHistory[tmsLaneHistorySelectedTierIndex]
    );
    if (!tier) return;

    setQuoteCards((prev) =>
      prev.map((card): QuoteCardType =>
        card.type !== tier.cardType
          ? card
          : ({
              ...card,
              cost: tier.cost,
              costPerMile: tier.costPerMile,
              priceRange: tier.priceRange,
              priceRangePerMile: tier.priceRangePerMile,
              inputtedTransportType: tier.inputtedTransportType,
              actualTransportType: tier.actualTransportType,
              tooltipContent: {
                ...card.tooltipContent!,
                timeframe: tier.timeframe,
                originType: tier.laneTier,
                destinationType: tier.laneTier,
              },
            } as QuoteCardType)
      )
    );

    if (selectedQuote === tier.cardType) {
      setCarrierCost(Math.round(tier.cost * 100) / 100);
    }
  }, [
    tmsLaneHistorySelectedTierIndex,
    tmsLaneHistory,
    selectedQuote,
    setQuoteCards,
    setCarrierCost,
  ]);

  // Auto-select a Quick Quote suggestion when none is active
  useEffect(() => {
    const firstQuickQuoteSuggestion = curSuggestionList.find(
      (s) =>
        s.pipeline === SuggestionPipelines.QuickQuote &&
        isQuoteRequestActionable(s)
    );
    const isClickedQQPipeline =
      clickedSuggestion?.pipeline === SuggestionPipelines.QuickQuote;

    // If no pending Quick Quote suggestions are available, clear the clickedSuggestion
    if (!firstQuickQuoteSuggestion) {
      if (isClickedQQPipeline) {
        setCurrentState((prevState) => ({
          ...prevState,
          clickedSuggestion: null,
        }));
      }
      return;
    }

    const clickedStillPending =
      clickedSuggestion &&
      isQuoteRequestActionable(clickedSuggestion) &&
      curSuggestionList.some(
        (s) => s.id === clickedSuggestion.id && isQuoteRequestActionable(s)
      );
    const userChoseQQCardStillInSet =
      isClickedQQPipeline && clickedStillPending;

    // Don't force the first QQ card while the user is reviewing another QQ card in the set.
    if (userChoseQQCardStillInSet) {
      return;
    }

    // User cleared selection by switching to a tab with no pending suggestion.
    if (!shouldKitAutoSelect && clickedSuggestion == null) {
      return;
    }

    // Only auto-select / advance within Quick Quote. Never steal selection when the
    // user explicitly chose another pipeline (e.g. Load Building).
    if (
      clickedSuggestion == null ||
      (isClickedQQPipeline && !clickedStillPending)
    ) {
      void goToSuggestion({
        suggestionID: firstQuickQuoteSuggestion.id,
      });
      return;
    }
  }, [
    curSuggestionList,
    goToSuggestion,
    clickedSuggestion,
    setCurrentState,
    shouldKitAutoSelect,
  ]);

  //  Handle FTL draft response updates
  useFTLDraftResponse(
    quoteMode,
    quoteResponse,
    finalFlatPrice,
    finalPriceFormat,
    maxDistance,
    fuelEstimate,
    draftResponse,
    userEditedDraft,
    currentPriceString,
    setDraftResponse,
    setCurrentPriceString,
    isQuoteCurrencyToggleEnabled ? formatQuoteAmount : undefined,
    isBillRateEditSessionActive
  );

  //  Handle LTL draft response updates
  useLTLDraftResponse(
    quoteMode,
    isLTLQuickQuoteEnabled,
    selectedLTLQuotes,
    ltlQuoteReplyDraftTemplateBody ||
      quoteResponse?.quoteReplyDraftTemplate?.body ||
      '',
    userEditedDraft,
    ltlExternalQuoteID,
    ltlExternalQuoteSource,
    isThreeGActive,
    getValues,
    setDraftResponse,
    setCurrentPriceString,
    quickQuoteConfig?.hideTaiLimitedLiability === true
  );

  // Reset when the active suggestion changes, not when defaults recompute (e.g.
  // Super Dispatch integrations finishing load in live/harness).
  const activeSuggestionResetKey =
    clickedSuggestion?.id ??
    clickedSuggestion?.sourceExternalID ??
    'no-suggestion';
  const lastSuggestionResetKeyRef = useRef(activeSuggestionResetKey);
  useEffect(() => {
    if (!memoizedDefaultValues) {
      return;
    }

    const showDates = shouldShowQuickQuoteDateFields(memoizedDefaultValues);

    if (lastSuggestionResetKeyRef.current !== activeSuggestionResetKey) {
      lastSuggestionResetKeyRef.current = activeSuggestionResetKey;
      reset(memoizedDefaultValues);
      setHasAddedQuoteDates(showDates);
      return;
    }

    if (showDates) {
      setHasAddedQuoteDates(true);
    }
  }, [activeSuggestionResetKey, memoizedDefaultValues, reset]);

  // Tai LTL page: seed pickup ready/close when entering LTL (e.g. FTL→LTL toggle).
  // Defaults only cover LTL suggestions; quoteMode is the page gate here.
  useEffect(() => {
    if (quoteMode !== QuoteMode.LTL || !isActiveTaiLtlIntegration) {
      return;
    }

    const readyRaw = getValues('pickupDate');
    const closeRaw = getValues('pickupCloseDate');
    const hasReady =
      readyRaw instanceof Date && !Number.isNaN(readyRaw.getTime());
    const hasClose =
      closeRaw instanceof Date && !Number.isNaN(closeRaw.getTime());

    const { pickupDate, pickupCloseDate } = resolveTaiLtlPickupWindow(
      hasReady ? readyRaw : null,
      hasClose ? closeRaw : null,
      undefined,
      quickQuoteConfig
    );
    // A date-only Ready moves onto the service's pickup hours.
    const shouldUpdateReady =
      !hasReady || pickupDate.getTime() !== readyRaw.getTime();
    const shouldUpdateClose =
      !hasClose || pickupCloseDate.getTime() !== closeRaw.getTime();

    if (!shouldUpdateReady && !shouldUpdateClose) {
      return;
    }

    if (shouldUpdateReady) {
      setValue('pickupDate', pickupDate, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }

    if (shouldUpdateClose) {
      setValue('pickupCloseDate', pickupCloseDate, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [
    quoteMode,
    isActiveTaiLtlIntegration,
    memoizedDefaultValues,
    quickQuoteConfig,
    getValues,
    setValue,
  ]);

  useEffect(() => {
    const suggestion = clickedSuggestion as Undef<SuggestedQuoteChange>;
    if (
      !suggestion ||
      suggestion.pipeline !== SuggestionPipelines.QuickQuote ||
      (!suggestion.sourceExternalID && !suggestion.sourceURL)
    ) {
      return;
    }

    const suggested = suggestion.suggested as Undef<QuoteChanges>;
    const toIso = (value: MaybeUndef<Date | string>): string => {
      if (!value) return '';
      const date = value instanceof Date ? value : new Date(value);
      return Number.isNaN(date.getTime()) ? '' : date.toISOString();
    };
    const loadSignature = JSON.stringify({
      identity: getQuickQuoteLoadIdentity(
        suggestion as QuickQuoteLoadIdentitySuggestion
      ),
      pickupCity: suggested?.pickupCity ?? '',
      pickupState: suggested?.pickupState ?? '',
      pickupZip: suggested?.pickupZip ?? '',
      deliveryCity: suggested?.deliveryCity ?? '',
      deliveryState: suggested?.deliveryState ?? '',
      deliveryZip: suggested?.deliveryZip ?? '',
      pickupDate: toIso(suggested?.pickupDate),
      deliveryDate: toIso(suggested?.deliveryDate),
    });

    const hasExistingQuoteResults =
      Boolean(quoteResponse) || quoteNotConfident || quoteCards.length > 0;

    if (lastPortalQuoteLoadSignatureRef.current === null) {
      lastPortalQuoteLoadSignatureRef.current = loadSignature;

      if (!hasExistingQuoteResults) {
        return;
      }
    } else if (lastPortalQuoteLoadSignatureRef.current === loadSignature) {
      return;
    }

    lastPortalQuoteLoadSignatureRef.current = loadSignature;
    resetQuoteState();
    // No card remains for the poll to update after this reset.
    cancelSuperDispatchPoll();
    setQuoteResponse(null);
    setQuoteNotConfident(false);
    setQuoteCards([]);
  }, [
    clickedSuggestion,
    quoteResponse,
    quoteNotConfident,
    quoteCards,
    resetQuoteState,
    cancelSuperDispatchPoll,
    setQuoteResponse,
    setQuoteNotConfident,
    setQuoteCards,
  ]);

  // Update quoteMode when suggestion changes
  useEffect(() => {
    if (!isLTLQuickQuoteEnabled) {
      setQuoteMode(QuoteMode.TL);
      return;
    }

    if (!isFTLQuickQuoteEnabled) {
      setQuoteMode(QuoteMode.LTL);
      return;
    }

    const suggestedFields = clickedSuggestion?.suggested as QuoteChanges;
    const mode =
      suggestedFields?.mode?.toUpperCase() === 'LTL'
        ? QuoteMode.LTL
        : QuoteMode.TL;
    setQuoteMode(mode);
  }, [
    clickedSuggestion,
    isLTLQuickQuoteEnabled,
    isFTLQuickQuoteEnabled,
    setQuoteMode,
  ]);

  const clickedSuggestionSourceExternalID = (
    clickedSuggestion as Undef<SuggestedQuoteChange>
  )?.sourceExternalID;
  const clickedSuggestionSourceURL = (
    clickedSuggestion as Undef<SuggestedQuoteChange>
  )?.sourceURL;
  const suggestedCustomerIdForPreselect =
    (clickedSuggestion?.suggested as Undef<QuoteChanges>)
      ?.customerExternalTMSID ?? null;
  const portalCustomerNameForPreselect =
    (
      clickedSuggestion?.suggested as Undef<
        QuoteChanges & { customer?: { name?: string } }
      >
    )?.customer?.name ?? null;

  // Fetch suggested customer from backend if it's not in the dropdown
  useEffect(() => {
    let cancelled = false;

    const hasIntegration =
      quoteMode === QuoteMode.LTL
        ? ltlTMSIntegrations?.[0]?.id
        : tmsIntegrations?.[0]?.id;
    const activeFTLTMS = tmsIntegrations?.[0]?.name as Undef<TMS>;
    const suggestionIdentity =
      clickedSuggestionSourceExternalID ||
      clickedSuggestionSourceURL ||
      (clickedSuggestionId ?? 'unknown');

    if (suggestedCustomerIdForPreselect && hasIntegration && quoteMode) {
      const existingCustomer = customersRef.current?.find(
        (c) => c.externalTMSID === suggestedCustomerIdForPreselect
      );
      const suggestedCustomerFetchKey = `${suggestionIdentity}:${suggestedCustomerIdForPreselect}`;

      if (
        !existingCustomer &&
        lastSuggestedCustomerFetchKeyRef.current !== suggestedCustomerFetchKey
      ) {
        lastSuggestedCustomerFetchKeyRef.current = suggestedCustomerFetchKey;

        // Search for the customer to get full details and add to dropdown
        const loadSuggestedCustomer = async () => {
          try {
            await fetchSuggestedCustomer(
              handleCustomerSearchRef.current,
              suggestedCustomerIdForPreselect
            );
          } catch (error) {
            captureException(error, {
              functionName: 'fetchSuggestedCustomer',
            });
          }
        };

        loadSuggestedCustomer();
      }
      return;
    }

    // Portal suggestions may only have shipper/customer name, not customerExternalTMSID.
    // In that case, search by name and preselect the first returned customer if available.
    if (
      !suggestedCustomerIdForPreselect &&
      quoteMode === QuoteMode.TL &&
      activeFTLTMS === TMS.McleodEnterprise &&
      hasIntegration &&
      portalCustomerNameForPreselect
    ) {
      const portalCustomerPreselectKey = `${suggestionIdentity}:${portalCustomerNameForPreselect}`;

      if (
        lastPortalCustomerPreselectKeyRef.current === portalCustomerPreselectKey
      ) {
        return () => {
          cancelled = true;
        };
      }

      lastPortalCustomerPreselectKeyRef.current = portalCustomerPreselectKey;

      const preselectCustomerByName = async () => {
        try {
          const searchResults = await handleCustomerSearchRef.current(
            'name',
            portalCustomerNameForPreselect
          );
          if (
            cancelled ||
            lastPortalCustomerPreselectKeyRef.current !==
              portalCustomerPreselectKey
          ) {
            return;
          }
          const firstResult = searchResults?.[0];
          if (firstResult?.value) {
            setValue('customerName', String(firstResult.value), {
              shouldDirty: false,
              shouldTouch: false,
              shouldValidate: false,
            });
          }
        } catch (error) {
          captureException(error, {
            functionName: 'preselectCustomerByName',
            portalCustomerName: portalCustomerNameForPreselect,
          });
        }
      };

      preselectCustomerByName();
    }

    return () => {
      cancelled = true;
    };
  }, [
    quoteMode,
    tmsIntegrations,
    ltlTMSIntegrations,
    setValue,
    clickedSuggestionId,
    clickedSuggestionSourceExternalID,
    clickedSuggestionSourceURL,
    suggestedCustomerIdForPreselect,
    portalCustomerNameForPreselect,
  ]);

  useEffect(() => {
    const isTurvoLTLFormActive = isTurvoActive && !isThreeGActive;
    if (
      quoteMode !== QuoteMode.LTL ||
      isTurvoLTLFormActive ||
      isActiveTaiLtlIntegration
    ) {
      return;
    }

    const stops = getValues('stops') ?? [];
    stops.forEach((stop, index) => {
      const normalizedLocation = getLTLDisplayLocation(stop as QuoteFormStop);
      if (
        normalizedLocation &&
        normalizedLocation !== (stop.location ?? '').trim()
      ) {
        setValue(`stops.${index}.location`, normalizedLocation, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      }
    });
  }, [
    quoteMode,
    isTurvoActive,
    isThreeGActive,
    isActiveTaiLtlIntegration,
    getValues,
    setValue,
  ]);

  useDistanceCalculation(
    quoteResponse,
    quoteNotConfident,
    clickedSuggestion,
    distanceSource,
    setMaxDistance,
    setDistanceSource,
    setRouteDetails,
    setStopFee,
    setIntermediateStopCount,
    lastAppliedStopFeeRef
  );

  useEffect(() => {
    if (carrierCostType === CarrierCostType.Flat) {
      setFinalFlatPrice(finalPrice);
    } else {
      setFinalFlatPrice((finalPrice ?? 0) * maxDistance);
    }
  }, [finalPrice, carrierCostType, maxDistance, setFinalFlatPrice]);

  useEffect(() => {
    if (
      selectedQuote !== SelectedQuoteType.DAT_LONGEST_LEG ||
      intermediateStopCount <= 0
    ) {
      return;
    }

    const prevFee = lastAppliedStopFeeRef.current;
    const diff = stopFee - prevFee;
    const deltaTrip = diff * intermediateStopCount;
    const deltaPerMile = maxDistance > 0 ? deltaTrip / maxDistance : 0;

    setQuoteCards((prev) =>
      prev.map((card): QuoteCardType => {
        if (card.type !== SelectedQuoteType.DAT_LONGEST_LEG) {
          return card;
        }

        const nextCost = (card.cost ?? 0) + deltaTrip;
        const nextCostPerMile =
          card.costPerMile != null && maxDistance > 0
            ? card.costPerMile + deltaPerMile
            : card.costPerMile;

        const nextPriceRange = card.priceRange
          ? {
              lowEstimate: card.priceRange.lowEstimate + deltaTrip,
              highEstimate: card.priceRange.highEstimate + deltaTrip,
            }
          : card.priceRange;

        const nextPriceRangePerMile =
          card.priceRangePerMile && maxDistance > 0
            ? {
                lowEstimate: card.priceRangePerMile.lowEstimate + deltaPerMile,
                highEstimate:
                  card.priceRangePerMile.highEstimate + deltaPerMile,
              }
            : card.priceRangePerMile;

        if (card.tooltipConstructor) {
          return {
            ...card,
            cost: nextCost,
            costPerMile: nextCostPerMile,
            priceRange: nextPriceRange,
            priceRangePerMile: nextPriceRangePerMile,
            tooltipContent: card.tooltipContent
              ? { ...card.tooltipContent, stopFeeUSDMedium: stopFee }
              : card.tooltipContent,
          };
        }

        return {
          ...card,
          cost: nextCost,
          costPerMile: nextCostPerMile,
          priceRange: nextPriceRange,
          priceRangePerMile: nextPriceRangePerMile,
        };
      })
    );
    lastAppliedStopFeeRef.current = stopFee;
  }, [
    selectedQuote,
    stopFee,
    intermediateStopCount,
    maxDistance,
    lastAppliedStopFeeRef,
    setQuoteCards,
  ]);

  useEffect(() => {
    if (selectedQuote !== SelectedQuoteType.DAT_LONGEST_LEG) {
      return;
    }

    const llCard = quoteCards.find(
      (c) => c.type === SelectedQuoteType.DAT_LONGEST_LEG
    );

    if (!llCard) {
      return;
    }

    let newBuy: number;
    if (carrierCostType === CarrierCostType.Flat) {
      newBuy = llCard.cost ?? 0;
    } else if (llCard.costPerMile != null) {
      newBuy = llCard.costPerMile;
    } else if (maxDistance > 0) {
      newBuy = (llCard.cost ?? 0) / maxDistance;
    } else {
      newBuy = 0;
    }

    if (newBuy !== carrierCost) {
      setCarrierCost(newBuy);
    }
  }, [
    selectedQuote,
    quoteCards,
    carrierCostType,
    maxDistance,
    carrierCost,
    setCarrierCost,
  ]);

  // Keep the DAT Longest Leg tooltip in sync with the current stop fee
  useEffect(() => {
    setQuoteCards((prev) =>
      prev.map((card) => {
        if (
          card.type === SelectedQuoteType.DAT_LONGEST_LEG &&
          card.tooltipContent
        ) {
          return {
            ...card,
            tooltipContent: {
              ...card.tooltipContent,
              stopFeeUSDMedium: stopFee,
            },
          };
        }
        return card;
      })
    );
  }, [stopFee, setQuoteCards]);

  return (
    <div>
      <ExtendedFormProvider aiDefaultValues={true}>
        <FormProvider {...formMethods}>
          <TooltipProvider>
            <form
              onSubmit={handleSubmit(onSubmitForm, onInvalidForm)}
              className='grid gap-5 grid-cols-1 mx-0 mb-4 w-full'
            >
              {isLTLQuickQuoteEnabled && isFTLQuickQuoteEnabled && (
                <QuoteModeToggle
                  quoteMode={quoteMode}
                  setQuoteMode={setQuoteMode}
                />
              )}

              {quoteMode === QuoteMode.TL ? (
                <>
                  <QuoteInformationSection
                    formMethods={formMethods}
                    transportTypeOptions={transportTypeOptions}
                    subTransportTypeOptions={subTransportTypeOptions}
                    isSubTransportTypesEnabled={
                      isQuickQuoteSubTransportTypesEnabled
                    }
                    tmsIntegrations={tmsIntegrations}
                  />

                  <StopsSection
                    formMethods={formMethods}
                    fields={fields}
                    remove={remove}
                    isMultiStopQuickQuoteEnabled={enableMultiStopUI}
                    hasAddedQuoteDates={hasAddedQuoteDates}
                    setHasAddedQuoteDates={setHasAddedQuoteDates}
                    handleAddStop={handleAddStop}
                  />

                  {isQuickQuoteCustomAccessorialsEnabled && (
                    <CustomAccessorialsSection />
                  )}
                </>
              ) : (
                <LTLFormRouter
                  formMethods={formMethods}
                  hasAddedQuoteDates={hasAddedQuoteDates}
                  accessorials={ltlAccessorials}
                  isLoadingAccessorials={isLoadingAccessorials}
                  transportTypeOptions={transportTypeOptions}
                  tmsIntegrations={tmsIntegrations}
                />
              )}

              {errors.root && (
                <Flex justify='center'>
                  <div className='w-auto text-sm border-error-500 bg-error-500 text-neutral-50 rounded-lg p-2 text-center'>
                    {errors.root.message}
                  </div>
                </Flex>
              )}

              {quoteMode === QuoteMode.TL ? (
                <>
                  <DATAccessSection
                    serviceUsesDAT={serviceUsesDAT}
                    hasGrantedDATPermissions={hasGrantedDATPermissions}
                    isLoadingDAT={isLoadingDAT}
                    setIsLoadingDAT={setIsLoadingDAT}
                    setHasGrantedDATPermissions={setHasGrantedDATPermissions}
                  />

                  <Button
                    buttonNamePosthog={ButtonNamePosthog.GetQuickQuote}
                    logProperties={getQuickQuoteProperties}
                    type='submit'
                    className='w-full'
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? <ButtonLoader /> : ButtonText.GetQuickQuote}
                  </Button>
                </>
              ) : (
                <Button
                  buttonNamePosthog={ButtonNamePosthog.GetLTLQuickQuote}
                  logProperties={getQuickQuoteProperties}
                  type='submit'
                  className='w-full'
                  disabled={isSubmitting || isLoadingLTLQuotes}
                >
                  {isSubmitting || isLoadingLTLQuotes ? (
                    <>
                      <ButtonLoader />
                      {isCheckingShiplifyForQuote
                        ? 'Checking Shiplify…'
                        : 'Getting LTL quotes…'}
                    </>
                  ) : (
                    ButtonText.GetLTLQuickQuote
                  )}
                </Button>
              )}

              {quoteMode === QuoteMode.TL &&
                quoteResponse &&
                !quoteNotConfident && (
                  <Flex
                    direction='col'
                    gap='sm'
                    align='center'
                    className='w-full pb-2'
                  >
                    <RouteDisplaySection />

                    {showKitCoach && (
                      <Flex direction='col' gap='sm' className='w-[95%]'>
                        <Flex align='center' gap='xs' className='mt-3'>
                          <DrumkitLogo
                            alt=''
                            className='h-4 w-4 shrink-0 object-contain'
                            aria-hidden
                          />
                          <Typography
                            variant='body-xs'
                            weight='semibold'
                            className='text-neutral-700 text-[14px]'
                          >
                            <Typography
                              as='span'
                              variant='body-xs'
                              weight='bold'
                              className='text-[14px] text-accent-600'
                            >
                              Kit
                            </Typography>
                            {'    Insights'}
                          </Typography>
                        </Flex>
                        <QuickQuoteCoach
                          quotes={quoteResponse?.quotes}
                          selected={isKitCoachSelected}
                          onSelect={handleKitCoachSelect}
                        />
                        <Flex align='center' gap='sm' className='w-full py-1'>
                          <Separator className='flex-1 bg-neutral-400' />
                          <Typography
                            variant='body-xxs'
                            className='shrink-0 text-neutral-500'
                          >
                            or select your source
                          </Typography>
                          <Separator className='flex-1 bg-neutral-400' />
                        </Flex>
                      </Flex>
                    )}

                    <QuoteCardsSection
                      setHasUserSelectedCard={setHasUserSelectedCard}
                      handleUpdateDATRate={handleUpdateDATRate}
                      suppressProviderSelection={isKitCoachSelected}
                      onProviderCardSelect={() => setIsKitCoachSelected(false)}
                    />
                  </Flex>
                )}

              {/* Lane History Charts */}
              {quoteMode === QuoteMode.TL &&
                (quoteNotConfident || quoteResponse) &&
                shouldShowLaneHistorySection && (
                  <>
                    <Typography
                      variant='h5'
                      weight='semibold'
                      className='text-neutral-800 whitespace-nowrap'
                    >
                      Lane History Trends
                    </Typography>

                    {showOnDemandDATLaneHistory && (
                      <DATLaneHistory
                        transportType={getValues('transportType')}
                        DATLaneHistoryData={DATLaneHistoryData}
                        setDATLaneHistoryData={setDATLaneHistoryData}
                        hasPulledDATLaneHistory={hasPulledDATLaneHistory}
                        setHasPulledDATLaneHistory={setHasPulledDATLaneHistory}
                      />
                    )}

                    {/* DAT charts first, then TMS (e.g. McLeod) lane history */}
                    {showLaneHistoryFromService && (
                      <>
                        {isLoadingLaneHistoryFromService && (
                          <Flex justify='center' gap='xs'>
                            <Typography variant='body-sm' textColor='muted'>
                              Fetching DAT Rate History...
                            </Typography>
                            <ButtonLoader />
                          </Flex>
                        )}
                        {!isLoadingLaneHistoryFromService &&
                          laneHistoryFromService && (
                            <>
                              <LaneHistoryFromServiceChart
                                months={laneHistoryFromService.months}
                                last7Days={laneHistoryFromService.last7Days}
                                title='DAT Rate History'
                                subtitle={titleCase(
                                  laneHistoryFromService.equipment
                                )}
                                description='Monthly rate trends with 7-day recent data.'
                              />
                              <Typography
                                variant='body-xs'
                                className='text-neutral-600 italic'
                              >
                                Showing 2-stop data from the first and last stop
                                in the form. Multi-stop coming soon.
                              </Typography>
                            </>
                          )}
                      </>
                    )}

                    {hasLaneHistoryTrendsContent ? (
                      <LaneHistoryTrends
                        toggleLaneHistoryGraph={toggleLaneHistoryGraph}
                      />
                    ) : null}

                    {shouldShowNoTmsLaneHistoryMessage && (
                      <Flex justify='center'>
                        <Typography
                          variant='body-sm'
                          className='text-neutral-500'
                        >
                          No TMS lane history available
                        </Typography>
                      </Flex>
                    )}
                  </>
                )}

              {quoteMode === QuoteMode.TL &&
                (quoteNotConfident || quoteResponse) && (
                  <>
                    <Flex direction='col' gap='sm' ref={rateBreakdownRef}>
                      <Typography
                        variant='h5'
                        weight='semibold'
                        className='text-neutral-800 whitespace-nowrap'
                      >
                        Rate Breakdown
                      </Typography>

                      <CarrierPriceCalculator
                        calculatorParent={
                          CarrierPriceCalculatorParent.QuickQuote
                        }
                        parentQuoteRequestId={parentQuoteRequestId}
                        showTitle={false}
                        mileage={maxDistance}
                        mileageSource={distanceSource}
                        finalPrice={finalPrice}
                        fuelEstimate={fuelEstimate}
                        datFuelSurcharge={datFuelSurcharge}
                        portalFuelSurchargeSource={
                          clickedSuggestion?.pipeline ===
                          SuggestionPipelines.QuickQuote
                            ? (clickedSuggestion?.source ?? null)
                            : null
                        }
                        portalFuelSurcharge={
                          clickedSuggestion?.pipeline ===
                          SuggestionPipelines.QuickQuote
                            ? clickedSuggestion?.suggested.fuelSurchargePerMile
                            : null
                        }
                        profit={profit}
                        setProfitHandler={setProfit}
                        profitType={profitType}
                        setProfitTypeHandler={setProfitType}
                        maxDistance={maxDistance}
                        terminatingActionHandler={handleTerminatingAction}
                        carrierCost={carrierCost}
                        carrierCostType={carrierCostType}
                        setCarrierCostTypeHandler={setCarrierCostType}
                        setCarrierCostHandler={setCarrierCost}
                        setFuelEstimateHandler={setFuelEstimate}
                        setFinalPriceHandler={setFinalPrice}
                        selectedQuickQuoteId={getSelectedQuickQuoteId()}
                        defaultFSCProvider={
                          quoteResponse && quoteResponse.configuration
                            ? quoteResponse.configuration.fscProvider
                            : ''
                        }
                        kencoFuelSurcharge={kencoFuelSurcharge}
                        customerTableFuelSurcharge={customerTableFuelSurcharge}
                        stopFee={stopFee}
                        selectedQuoteType={
                          isKitCoachSelected
                            ? undefined
                            : (selectedQuote ?? undefined)
                        }
                        availableCustomAccessorials={
                          availableCustomAccessorials
                        }
                        selectedCustomAccessorials={
                          customAccessorialsWatched ?? []
                        }
                      />
                    </Flex>

                    {bidFlowEligibleBase && !isOnQuotingPortal && (
                      <BidFlowToggle
                        value={bidFlowMode}
                        onValueChange={setBidFlowMode}
                      />
                    )}

                    {shouldShowSubmitBidSection && (
                      <SubmitBidSection
                        key={submitBidContextKey}
                        integrationName={defaultServerSubmitPortalName}
                        fuelEstimate={fuelEstimate}
                        submitBidUsesLinehaul={
                          finalPriceFormat === CarrierCostType.Linehaul
                        }
                        normalizedLoadId={normalizedSubmitBidLoadId}
                        tmsCustomerId={
                          (clickedSuggestion?.suggested as QuoteChanges)
                            ?.customerExternalTMSID
                        }
                        customerName={customerNameForCredentialOptions}
                        credentialOptionsByPortal={credentialOptionsByPortal}
                        isPortalCredentialBatchLoading={
                          isPortalCredentialBatchLoading
                        }
                        submitBidStatus={submitBidStatus}
                        isSubmitBidResultPending={isSubmitBidResultPending}
                        onResolvedSubmitBidUsesLinehaul={
                          setResolvedSubmitBidUsesLinehaul
                        }
                        onSelectedPortalChange={
                          handleSelectedServerSubmitPortalChange
                        }
                        resolvedSubmitBidBasisResetSignal={
                          submitBidResolvedBasisResetSignal
                        }
                        onSubmitBid={handleSubmitBidSelection}
                        isSubmittingBid={isSubmittingToPortal}
                      />
                    )}
                  </>
                )}

              {/* LTL Quote Results */}
              {shouldShowLtlRatesSection && (
                <>
                  <div className='w-full h-0.5 bg-neutral-300 my-2' />

                  <Typography
                    variant='h5'
                    weight='semibold'
                    className='text-neutral-800'
                  >
                    LTL Rates
                  </Typography>

                  {isLoadingLTLQuotes && (
                    <Flex
                      justify='center'
                      align='center'
                      gap='xs'
                      className='py-4'
                    >
                      <Typography variant='body-sm' textColor='muted'>
                        Fetching LTL quotes...
                      </Typography>
                      <ButtonLoader />
                    </Flex>
                  )}

                  {shouldShowEmptyLtlQuotesMessage && (
                    <Flex justify='center' align='center' className='py-4'>
                      <Typography variant='body-sm' textColor='muted'>
                        {ltlEmptyQuotesMessage}
                      </Typography>
                    </Flex>
                  )}

                  {shouldShowProviderLtlFailure && !isLoadingLTLQuotes && (
                    <Typography
                      variant='body'
                      weight='medium'
                      className='text-error-600 py-4'
                    >
                      {ltlProviderFailureMessage ??
                        ltlOutcomeConfig?.providerFailureMessage}
                    </Typography>
                  )}

                  {ltlQuoteFetchError && !isLoadingLTLQuotes && (
                    <Typography
                      variant='body'
                      weight='medium'
                      className={cn(
                        'py-4',
                        ltlInlineSystemFailureLevel === 'warning'
                          ? 'text-warning-700'
                          : 'text-error-600'
                      )}
                    >
                      {ltlInlineSystemFailureMessage || ltlSystemFailureMessage}
                    </Typography>
                  )}

                  {!isLoadingLTLQuotes &&
                    !ltlQuoteFetchError &&
                    !shouldShowProviderLtlFailure &&
                    ltlQuotes.length > 0 && (
                      <LTLQuoteResults
                        onSelectQuote={handleSelectLTLQuote}
                        isActiveTaiLtlIntegration={isActiveTaiLtlIntegration}
                      />
                    )}

                  {/* Show Draft Reply for LTL when quotes selected (not on bidding portals). */}
                  {selectedLTLQuotes.length > 0 &&
                    !isLoadingLTLQuotes &&
                    !isOnQuotingPortal && (
                      <>
                        <div className='w-full h-0.5 bg-neutral-300 my-2' />
                        <DraftResponseSection
                          handleDraftResponse={handleDraftResponse}
                          handleSendResponse={handleSendResponse}
                          copyToClipboardProperties={copyToClipboardProperties}
                          showLinehaulOption={false}
                          onCopySuccess={() => {
                            setHasCopiedDraftResponse(true);

                            // Non-blocking call to update quote request suggestion with the status and the final quote the user uses.
                            handleTerminatingAction(
                              QuoteTerminatingAction.Copy,
                              QuoteValueType.TotalSell
                            );

                            setTimeout(
                              () => setHasCopiedDraftResponse(false),
                              2000
                            );
                          }}
                        />
                      </>
                    )}
                </>
              )}
            </form>

            {quoteMode === QuoteMode.TL &&
              quoteResponse &&
              quoteResponse.configuration?.belowThresholdMessage && (
                <div className='w-auto text-sm border-error-500 bg-error-500 text-neutral-50 rounded-lg p-2 mb-4 text-center'>
                  {quoteResponse.configuration?.belowThresholdMessage}
                </div>
              )}

            {quoteMode === QuoteMode.TL &&
              (quoteResponse || quoteNotConfident) && (
                <div
                  className='flex-col justify-center'
                  ref={scrollResultsIntoViewRef}
                >
                  {shouldShowBottomDraftResponse && (
                    <DraftResponseSection
                      handleDraftResponse={handleDraftResponse}
                      handleSendResponse={handleSendResponse}
                      copyToClipboardProperties={copyToClipboardProperties}
                      showLinehaulOption={true}
                      onCopySuccess={() => {
                        setHasCopiedDraftResponse(true);

                        const quotedValueType =
                          finalPriceFormat === CarrierCostType.Linehaul
                            ? QuoteValueType.Linehaul
                            : QuoteValueType.TotalSell;

                        // Non-blocking call to update quote request suggestion with the status and the final quote the user uses.
                        handleTerminatingAction(
                          QuoteTerminatingAction.Copy,
                          quotedValueType
                        );

                        setTimeout(
                          () => setHasCopiedDraftResponse(false),
                          2000
                        );
                      }}
                    />
                  )}

                  <QuoteSubmissionSection
                    formMethods={formMethods}
                    showSubmitToPortalButtons={showSubmitToPortalButtons}
                    portalInfo={portalInfo}
                    isSubmittingToPortal={isSubmittingToPortal}
                    fuelEstimate={fuelEstimate}
                    newCustomerURLInTMS={newCustomerURLInTMS}
                    quoteURLInTMS={quoteURLInTMS}
                    tmsName={tmsName}
                    portal={portal}
                    handleSubmitToPortal={handleSubmitToPortal}
                    handleSubmitQuoteToTMS={handleSubmitQuoteToTMS}
                    handleQuoteSubmissionViaURL={() => {
                      const currentSelectedQuickQuoteIdForURL =
                        getSelectedQuickQuoteId();
                      submitQuoteViaURL(currentSelectedQuickQuoteIdForURL);
                    }}
                  />
                </div>
              )}
          </TooltipProvider>
        </FormProvider>
      </ExtendedFormProvider>

      <FloatingSellRate
        rateBreakdownRef={rateBreakdownRef}
        finalPrice={finalPrice}
        carrierCost={carrierCost}
        carrierCostType={carrierCostType}
        profit={profit}
        profitType={profitType}
        enabled={
          quoteMode === QuoteMode.TL &&
          Boolean(quoteNotConfident || quoteResponse)
        }
      />
    </div>
  );
}
